import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import * as scoreUtil from "core/src/core/score/score-util.js";
import * as gsf from "core/src/core/game-state-functions.js";
import { sideOfKey } from "core/src/core/arena-sizes.js";
import { wormColors } from "core/src/core/constants.js";
import { keyPairs } from "../../../shared/key-util.js";

import { connectToServer, getClientId, getTabId, clearStickyRoom, refuseStickyRoom, hasStickyRoom, stickyFor, PlayOptions, ChatMessage } from "../../../online/net";
import { endResuming, forgetGame, gameDown, gameUp, isResuming, rememberGame, type ResumeKind } from "../../../online/resume";
import { createRemoteGame } from "../../../online/remote-game";
import { createOwnWormController } from "../../../online/prediction";
import PlayerSteeringListener from "./player-steering-listener.js";
import TouchSteeringOverlay from "./TouchSteeringOverlay";
import GameCanvas from "../../../game-canvas/GameCanvas";
import Countdown from "../../../game-canvas/overlays/Countdown";
import RespawnCountdown from "../../../game-canvas/overlays/RespawnCountdown";
import { buildDeathReport } from "../../../online/death-report";
import { reportError, reportSignal, setMonitoringContext } from "../../../shared/monitoring";
import MatchReplay from "../replay/MatchReplay";
import { fetchReplay as fetchMatchReplay } from "../../../shared/replays";
import SpawnTags from "../../../game-canvas/overlays/SpawnTags";
import { introSchedule, introLeaders, type IntroSchedule } from "../../../shared/round-intro";
import { isTieBreak } from "../../../shared/tie-break";
import { hostCadence } from "core/src/ai/crowd.js";
import Overlay from "../../../game-canvas/overlays/Overlay";
import RoundOverCard, { contentFadeFor } from "../round-over/RoundOverCard";
import DuelFightCard from "../round-over/DuelFightCard";
import MatchOver from "../match-over/MatchOver";
import type { Unlock } from "../match-end/UnlockCelebration";
import MatchEndModal from "../match-end/MatchEndModal";
import AchievementProgress, { type AchievementMove } from "../match-end/AchievementProgress";
import { rankedLabel } from "../../../shared/rating/ranked";
import { track } from "../../../shared/analytics";
import FirstRunHints from "../../../game-canvas/overlays/FirstRunHints";
import { useFirstRun, useHintPhase, noteMatchStarted } from "../../../shared/first-run";
import gameStyles from "../play.module.scss";
import Score from "./Score";
import GameHud from "./GameHud";
import { matchPlaystyle } from "../../setup/presets";
import GameLayout from "./GameLayout";
import { useIsTouch } from "../../../shared/useResponsive";
import musicController from "../../../audio/music-controller";
import { setMatchBoardSkin } from "../../../game-canvas/board-skins";
import { computeGameAudio } from "../../../audio/game-audio";
import { padWinners } from "../../../shared/round-winners";
import { GAME_VERSION, BUILD_LABEL } from "../../../shared/version";
import { getLocalPlayer } from "../../../shared/local-players";
import { useAuth } from "../../../shared/auth";
import { isAdmin } from "../../../shared/admin";
import { createWigglePeer } from "../../../online/wiggle-peer";
import { showToast } from "../../../shared/toast";
import { saveMatchEndSnapshot, loadMatchEndSnapshot, clearMatchEndSnapshot } from "../../../online/match-end-snapshot";
import { dynamicKindOfRoom, type NextRound, type RoundResult, type RoundSetup, type RoyaleResult } from "../../../online/dynamic";
import DynamicLayer from "../dynamic/DynamicLayer";
import { Opening as RoyaleOpening } from "../dynamic/RoyaleMoments";
import { keepGuestRoyaleResult } from "../../../shared/royale";
import { takeProgressUpdate, takeGuestRound, resetMatchProgress } from "../../../shared/progress";
import { refreshUnopened } from "../../../shared/achievements-open";
import { useWakeLock } from "../../../shared/useWakeLock";

// The player's time zone, for the achievements that count calendar days (the
// server works out "your day" with it). null when the browser won't say.
function localTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

// One entry per local human sharing this device (1 or 2). Name/colour/keys come
// from the shared local-players store (kept in sync with the lobby + Local setup).
interface LocalEntry { name: string; colorId: string; left: string | null; right: string | null; flag?: string; victoryAnim?: string | null; headStyle?: string | null; }

interface Props {
  // Match options offered to the server (defaults to one bot).
  options?: PlayOptions;
  onLeave: () => void;
  // Matchmaking boxes only: the results screen's Play again. Runs after this
  // tab's sticky room and results snapshot are dropped, so the caller can
  // rejoin the box fresh.
  onPlayAgain?: () => void;
  // Royale only: go and play Play Now (while waiting for the next one).
  onPlayNow?: () => void;
  // Explicit room to join (overrides ?room=). Used when the lobby renders this
  // session inline for a matchmaking box.
  roomId?: string;
  // When provided, this session is "embedded" (rendered over the lobby): instead
  // of drawing its own waiting screen, it reports waiting-room state up so the
  // lobby can show the modal. The game itself still renders here once it starts.
  onSession?: (s: OnlineSession) => void;
  // Reports the concrete room instance once a public base room resolves through the
  // matchmaker, so the page can put the match id in the URL (reload/share anchor).
  onRoomInstance?: (instance: string) => void;
  // Custom lobby host config, sent with the first `play`. Only the host (first
  // joiner) is honored by the server; later joiners pass it harmlessly. Carries the
  // full match rules so the online match plays as the host configured it.
  lobbyConfig?: {
    maxPlayers?: number;
    botFill?: boolean;
    public?: boolean;
    maxScore?: number;
    maxRounds?: number;
    disabledPowerUps?: string[];
    powerUpSpawnChance?: number;
    wormSpeed?: number;
  };
  // Override the couch players (name/colour/keys) seated on this device. When set,
  // these are used instead of the shared 2-slot store, so a lobby can seat more than
  // two couch players, each with its own key bindings.
  localPlayers?: LocalEntry[];
}

// Seconds between a round's last crash and its round-over sting.
const ROUND_STING_DELAY = 0.2;

type Phase = "connecting" | "waiting" | "playing" | "countdown" | "over" | "disconnected";

// How fast the own-worm clock may close its drift to the server during the start
// phase, as a share of real time: the countdown runs at most this much fast or slow.
const START_SLEW = 0.1;
// How long we stay connected after match_over: the rating and replay messages that
// follow it land within seconds, then we hang up (see the match_over handler).
const POST_MATCH_LINGER_MS = 30000;
// Whether this page load came from a browser RELOAD (vs a navigation/click). Decides
// what a bounced sticky room means: a reload into a finished/gone match should land
// quietly on the home screen, while a deliberate visit with a stale sticky instance
// (e.g. opening /online again later in the same tab) should retry a fresh room.
const NAV_IS_RELOAD = (() => {
  try {
    const e = performance.getEntriesByType("navigation")[0] as any;
    return !!e && e.type === "reload";
  } catch {
    return false;
  }
})();

// Snapshot of the waiting room, pushed to an embedding lobby (see onSession).
export interface OnlineSession {
  phase: Phase;
  players: any[];      // roster (humans + bots) from the server lobby_update
  roomName?: string | null; // a Game Room's name, as its host set it (null: none)
  startAt: number;     // ms epoch when the match starts at the latest
  etaAt: number;       // ms epoch when a public box will most likely launch (0 if unknown)
  youIds: string[];    // this client's own playerId(s)
  // A ranked room: every seat's ladder rating (null while placing), once the
  // server's ranked_roster has landed. undefined/null before that.
  rankedRatings?: Record<string, { rating: number | null; games: number }> | null;
  startNow: () => void; // ask the server to start the box immediately
  // Custom-lobby fields (a `lobby:CODE` room): the host controls the start.
  isHost: boolean;     // are we the host? (our id is the server's hostPlayerId)
  start: () => void;   // host: start the match now (start_game)
  code?: string;       // the shareable lobby code (CODE from lobby:CODE)
  chat: ChatMessage[]; // lobby chat log (player + system lines)
  sendChat: (text: string) => void; // send a chat message
  map?: any;           // the lobby's map descriptor (from lobby_update)
  maxPlayers?: number; // the lobby's capacity (from lobby_update)
  botFill?: boolean;   // whether empty seats auto-fill with bots on start
  botFillSkill?: number; // difficulty (1-10) those auto-fill bots use
  updateConfig: (cfg: any) => void;  // host: push edited settings to the open lobby
  setLocals: (list: any[]) => void;  // add/remove couch players on this device
  addBot: (skill?: number) => void;  // host: add a bot (skill 1-10) to the lobby
  kickBot: (id: string) => void;     // host: remove a bot by id
  kickPlayer: (id: string) => void;  // host: kick a joined player by one of their ids
  config?: any;      // the host's full match config (for a guest's read-only view)
}

// The networked counterpart of LocalGame: the server runs the authoritative
// simulation and streams state, we render it slightly delayed and forward our
// steering. Self-contained for the first online milestone (one human vs server
// bots); it does not go through App's local match-flow state machine.
function RemoteGame({ options, onLeave, onPlayAgain, onPlayNow, roomId, onSession, onRoomInstance, lobbyConfig, localPlayers: localPlayersProp }: Props) {
  const touch = useIsTouch();
  const embedded = !!onSession;

  // Tuning knobs from the URL so we can iterate without rebuilds:
  //   ?bots=3      number of server-side bots (mock opponents), default 1
  //   ?delay=80    render delay in ms (smoothness lever), default 100
  //   ?name=Foo    your display name
  //   ?server=...  point at a specific edge-server (else VITE_DS_SERVER_URL)
  //   ?wiggle=N    admin-only: add N self-driving "human" test opponents (blue)
  //   ?wigglelag=220 ?wigglejitter=80   simulate the peers on a worse connection (ms)
  const [search] = useSearchParams();
  const botCount = clampInt(search.get("bots"), 1, 0, 29);
  const renderDelayMs = clampInt(search.get("delay"), 100, 0, 1000);
  // Netstats overlay (F9): the adaptive render-delay internals, live. Available to
  // everyone (read-only diagnostics; harmless and handy for lag reports).
  // F9 toggles it; ?netstats=1 opens it from the start (no function key needed).
  const [netStats, setNetStats] = useState(() => { try { return new URLSearchParams(window.location.search).get("netstats") === "1"; } catch { return false; } });
  const interpStatsRef = useRef<((serverNow: number) => any) | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "F9") setNetStats((v) => !v); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const name = search.get("name") || options?.name;
  const colorId = search.get("color") || options?.colorId;
  const serverOverride = search.get("server");
  // Map preset (?map=small|medium|large, "duel" = small) or an explicit ?size=N.
  // Smaller maps draw each worm bigger on screen, handy for spotting netcode detail.
  const mapSizes: Record<string, number> = { small: sideOfKey("small"), duel: sideOfKey("medium"), medium: sideOfKey("medium"), large: sideOfKey("large"), huge: sideOfKey("huge") };
  const sizeParam = search.get("size");
  const mapParam = (search.get("map") || "").toLowerCase();
  let map: PlayOptions["map"] | undefined;
  // (The equal-area 2:1 rectangle for that size, like the public boxes.)
  if (sizeParam) map = { type: "Rectangle", ...gsf.equalAreaRectangle(clampInt(sizeParam, 1000, 300, 3000)) };
  else if (mapSizes[mapParam]) map = { type: "Rectangle", ...gsf.equalAreaRectangle(mapSizes[mapParam]) };
  const debug = search.get("debug") === "1";
  // ?bench=N: ask a named room for the dev-only perf bench (no crashes, wall hack all
  // round, filled to N players). The edge-server ignores it unless it runs with
  // PERF_BENCH=1 (local wrangler dev), so it does nothing in production.
  const benchFillTo = search.get("bench") != null ? clampInt(search.get("bench"), 2, 2, 40) : null;
  // ?ghost=1: overlay a ring where the SERVER has each worm's head (server-now, no
  // render delay), to see the gap vs the delayed/predicted view. Pair with ?netlag=.
  const ghost = search.get("ghost") === "1";
  // Client-authoritative own worm is the default now; ?predict=0 disables it.
  const predict = search.get("predict") !== "0";
  // Per-opponent adaptive delay: render each opponent on its own MEASURED clock
  // (cushion sized to that opponent's observed jitter, see interpolation.ts) so a
  // clean stream renders near-fresh and one laggy player looks laggy without
  // delaying everyone. On by default; ?adapt=0 falls back to the single global
  // delay. ?adaptmargin=ms forces a minimum cushion (default 0: the measured
  // peak + pad IS the floor - it measures exactly the between-arrival age a
  // fixed floor could only guess at).
  const adaptiveDelay = search.get("adapt") !== "0";
  const adaptMarginSec = clampInt(search.get("adaptmargin"), 0, 0, 1000) / 1000;
  // Admin-only test aid: ?wiggle=N adds N self-driving "human" opponents that join
  // this same room and steer in a slow left/right wobble (see wiggle-peer.ts), so we
  // can test how a real remote human renders without a second person. Admin-gated so
  // it never spawns fake players for the public.
  const { user, session } = useAuth();
  const wiggleCount = isAdmin(user) ? clampInt(search.get("wiggle"), 0, 0, 8) : 0;
  // Simulated bad-connection lag for the wiggle peers (ms, one-way) + jitter, so
  // they mimic a phone on poor wifi. Defaults to a noticeably laggy 220ms / 80ms.
  const wiggleLagMs = clampInt(search.get("wigglelag"), 220, 0, 2000);
  const wiggleJitterMs = clampInt(search.get("wigglejitter"), 80, 0, 1000);
  // Test peers run a clean hole-free trail by default so gaps can only be a bug, not
  // a jump; ?wigglejump=1 makes them leave real holes like a normal worm.
  const wiggleJumps = search.get("wigglejump") === "1";
  // Shared lobby room: a matchmaking box (roomId prop) or ?room=NAME. No room =
  // solo vs bots (starts immediately).
  const room = roomId ?? (search.get("room") || undefined);
  // Custom lobby? A "lobby:CODE" room. The CODE is the shareable join code.
  const lobbyCode = room && room.startsWith("lobby:") ? room.slice("lobby:".length) : undefined;
  // Rated? Only the public matchmaking boxes are (the server's launch rule, see
  // edge-server startMatch). Everything else (a custom lobby, a named room,
  // solo) skips the Match End rating modal and goes straight to the results.
  // A changing room (Play Now, Royale: online/dynamic.ts) is never rated.
  const dynKind = dynamicKindOfRoom(room);
  // the screen stays on while the game is up (played, between rounds, or watched)
  useWakeLock();
  useEffect(() => { setMonitoringContext({ room: dynKind || (room || "").split("#")[0] || null }); return () => setMonitoringContext({ room: null }); }, [room, dynKind]);
  const rated = !!room && room.startsWith("public:") && !dynKind;
  // which kind of game this is for coming back to it after a restart (online/resume.ts)
  const resumeKind: ResumeKind | null = dynKind === "royale" || dynKind === "playnow" ? dynKind : room && /^public:ranked-/.test(room) ? "ranked" : null;


  // A DELIBERATE exit (leave button, results exit, a bounce): release this tab's sticky
  // room instance so the next join matchmakes fresh, then leave. A reload never runs
  // this, which is exactly what lets it reconnect into the same match.
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  const leaveSession = useCallback(() => {
    clearStickyRoom(room);
    forgetGame(resumeKind || undefined);
    clearMatchEndSnapshot();
    onLeaveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);
  const onPlayAgainRef = useRef(onPlayAgain);
  onPlayAgainRef.current = onPlayAgain;
  const playAgain = useCallback(() => {
    clearStickyRoom(room);
    forgetGame(resumeKind || undefined);
    clearMatchEndSnapshot();
    onPlayAgainRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  // By default one human plays from this device: player 1 of the store (name and
  // colour may be overridden via ?name= / ?color=). A page that lets several
  // share the keyboard (Play Now, a game room) passes them as `localPlayers`.
  const storeLocalPlayers: LocalEntry[] = useMemo(() => {
    const count = 1;
    const out: LocalEntry[] = [];
    for (let i = 0; i < count; i++) {
      const p = getLocalPlayer(i, "Player " + (i + 1));
      out.push({ name: p.name, colorId: p.colorId, left: p.left, right: p.right, flag: p.flag, victoryAnim: p.victoryAnim ?? null, headStyle: p.headStyle ?? null });
    }
    if (name && out[0]) out[0].name = name;
    if (colorId && out[0]) out[0].colorId = colorId;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The couch players seated on this device: the explicit prop (N couch players
  // + their keys) if given, else player 1 alone. Read via a
  // ref so a live add/remove is reflected at the next round's steering (re)bind.
  const localPlayersRef = useRef<LocalEntry[]>(storeLocalPlayers);
  localPlayersRef.current = localPlayersProp && localPlayersProp.length ? localPlayersProp : storeLocalPlayers;

  const [phase, setPhase] = useState<Phase>("connecting");
  // Back into this game after an accidental close (online/resume.ts): while in a
  // Ranked match, a Royale or Play Now, the device remembers it (and keeps the
  // time fresh); leaving the game's page any other way forgets it. A closed app
  // never runs the unmount, which is what keeps it remembered.
  useEffect(() => {
    if (!resumeKind || phase === "connecting" || phase === "over" || phase === "disconnected") return;
    if (isResuming()) { endResuming(); showToast("Back in your game"); }
    if (resumeKind === "royale" && myPlaceRef.current != null) return; // out: forgotten already
    const save = () => {
      if (resumeKind === "royale" && myPlaceRef.current != null) return;
      const st = stickyFor(room);
      rememberGame({ kind: resumeKind, path: window.location.pathname + window.location.search, room: room || "", instance: st.instance, origin: st.origin, at: Date.now() });
    };
    save();
    const id = window.setInterval(save, 10000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeKind, phase, room]);
  useEffect(() => {
    if (!resumeKind) return;
    gameUp(resumeKind);
    return () => { gameDown(resumeKind); forgetGame(resumeKind); };
  }, [resumeKind]);
  // Bumped to tear down + rebuild the socket connection (the mount effect keys on it).
  // Used when a STALE sticky room instance (a finished match from an earlier visit in
  // this tab) answers room_unavailable: drop the sticky and rejoin fresh, once.
  const [connectEpoch, setConnectEpoch] = useState(0);
  const retriedStickyRef = useRef(false);
  // Latest phase for the socket-effect closures (which capture stale state).
  const phaseRef = useRef<Phase>("connecting");
  phaseRef.current = phase;
  // True only while a round is actually live. Set synchronously at game_start / game_over
  // so a crash in the game_over -> game_countdown window (where `phase` still reads
  // "playing" until its state update lands) isn't mis-reported to the server.
  const roundActiveRef = useRef(false);
  const openingAtRef = useRef(0);
  const [, force] = useState(0);
  const forceRender = () => force((t) => t + 1);
  // Bumped when the waiting-room roster/ids change, so the session-report effect
  // re-pushes to an embedding lobby (see onSession).
  const [sessionTick, setSessionTick] = useState(0);
  const bumpSession = () => setSessionTick((t) => t + 1);
  const onSessionRef = useRef(onSession);
  onSessionRef.current = onSession;
  const startNowRef = useRef<() => void>(() => {});

  // Stable across renders: the remote game (mutated in place by deltas), the
  // socket, and per-match bookkeeping the render path reads through refs.
  const remoteRef = useRef<ReturnType<typeof createRemoteGame> | null>(null);
  if (!remoteRef.current) remoteRef.current = createRemoteGame(renderDelayMs / 1000);
  const matchConfigRef = useRef<any>(null);
  // The network playerIds assigned to this client (1 per local human on this device).
  // In a changing room these are only the seats playing THIS round (none while
  // watching: just dropped in, or knocked out); seatIdsRef holds every seat.
  const localIdsRef = useRef<string[]>([]);
  const seatIdsRef = useRef<string[]>([]);
  // Changing rooms (online/dynamic.ts): the current round, the next one (during
  // the pause), what the finished round meant, and a Royale's final result.
  const pendingRef = useRef(false);
  const setupRef = useRef<RoundSetup | null>(null);
  const nextRef = useRef<NextRound | null>(null);
  const roundResultRef = useRef<RoundResult | null>(null);
  const royaleResultRef = useRef<RoyaleResult | null>(null);
  const knownPlayersRef = useRef<Record<string, any>>({});
  const myPlaceRef = useRef<number | null>(null);
  const pauseAtRef = useRef(0);
  const movedRef = useRef(false);
  const dynRetriesRef = useRef(0);
  const royaleSavedRef = useRef(0); // bumped when the server has saved our Royale result
  // The match's 1-based round (0 until the first game_start), read by the
  // render path below.
  const roundRef = useRef(0);
  // First-run hints (first-run.ts): new players get the steering walked
  // through during the FIRST round's start phase, and from round two on the
  // board looks like everyone else's. Not for two players on one phone (the
  // zones are shared then). Hooks, so above the early returns below.
  const firstRun = useFirstRun(user?.id, roundRef.current);
  const hint = useHintPhase(
    firstRun && phase === "playing" && (!touch || localIdsRef.current.length === 1),
    remoteRef.current ? remoteRef.current.getGameState() : null,
    () => (remoteRef.current ? remoteRef.current.getRenderTime() : 0),
    touch
  );
  const startScoreRef = useRef<any>({});
  // The score at the START of the current round, for the scoreboard. Unlike
  // startScoreRef it is not advanced at game_over (the scoreboard adds the
  // round's gain on top of it, and settles to roundOverScoreRef), only when
  // the next round starts.
  const roundStartScoreRef = useRef<any>({});
  // The round-start roll call schedule for the current round (round-intro.ts),
  // rebuilt at every game_start; the canvas renderers read it through a ref.
  const introScheduleRef = useRef<IntroSchedule>({ delays: {}, fadeAt: 0, fadeDur: 0, end: 0 });
  const introDelaysRef = useRef<Record<string, number>>({});
  // Set at game_over to the server's settled accumulated score; shown directly
  // (no render-delayed recompute) until the next round starts.
  const roundOverScoreRef = useRef<any>(null);
  // Each round's winner in order, for the duel dots (GameHud). A reconnect
  // mid-match only brings the score: padWinners fills the gap from it.
  const roundWinnersRef = useRef<string[]>([]);
  // Between-rounds round-over overlay: the round winner + a countdown to the next
  // round (mirrors local play's GameOver screen).
  const roundWinnerRef = useRef<any>(null);
  // The same, kept for a changing room's own between-round screens (which may
  // take the ordinary card's place, see game_over).
  const roundWinnerDynRef = useRef<any>(null);
  const roundWinnerPointsRef = useRef<number | null>(null);
  // The match score going into the round that just ended (the ranked duel's
  // fight card ticks the winner's digit up from it).
  const roundBeforeScoreRef = useRef<any>(null);
  const matchStatsRef = useRef<any>(null);
  // Players who left the match before it ended (match_over `left`): ranked last.
  const leftIdsRef = useRef<string[]>([]);
  const matchWinnerRef = useRef<any>(null);
  const nextRoundAtRef = useRef(0);
  const countdownSecsRef = useRef(5); // the wait's full length (the board's content fade is timed to it)
  // True during the LAST round's countdown: the lap plays out and shows "Final
  // results in N" (then match_over), instead of "Next round in N".
  const finalCountdownRef = useRef(false);
  // Bridges the socket's steering emit out of the effect so touch controls can use it.
  const sendSteeringRef = useRef<(playerId: string, s: number) => void>(() => {});
  // One client-authoritative controller per owned worm, keyed by playerId.
  const predictorsRef = useRef<Record<string, any>>({});
  // Estimated one-way latency (seconds), measured via RTT pings. Used to align the
  // prediction clock with the server so the worm is drawn at true "now".
  const oneWayRef = useRef(0);
  // Debug (?debug=1) + netstats (F9): game_update arrival bookkeeping. `maxGap` is
  // the drain-on-read worst gap for the ?debug overlay; `gaps` is a rolling ring of
  // recent inter-arrival gaps (~10s at typical cadence) for the netstats overlay -
  // packet arrival frequency/reliability is THE input the adaptive cushion answers.
  const updGapRef = useRef({ last: 0, maxGap: 0, gaps: [] as number[] });
  // The host's pace, as seen from here: each game_update's arrival (wall clock)
  // and its game time, plus the host's tick count since its last push. A ring
  // of the last ~10s for the netstats overlay's "host" line: game seconds per
  // wall second (1.0 = real time; a CPU-bound host runs the match in slow
  // motion) and ticks per second. Measured on the client on purpose: a Durable
  // Object's clock stands still while it runs CPU-bound code, so the host can't
  // time itself.
  const hostPaceRef = useRef<{ wall: number; gameTime: number; ticks: number; sentAt: number }[]>([]);
  // The accurate current server time (estimate + one-way latency).
  const serverNow = () => remoteRef.current!.getServerTime() + oneWayRef.current;
  // PURE-LOCAL clock for the OWN worm, anchored at the round's start. Your worm is
  // client-authoritative, so its timeline is just real elapsed time since the round
  // began - exactly like offline play accumulates frame deltas. Driving prediction
  // from this (instead of the server clock + measured latency) removes the residual
  // network/server-clock jitter, so your worm feels 100% local. A safety re-anchor
  // keeps it from drifting far from the server estimate over a long round.
  const ownClockRef = useRef<{ t0: number; wall0: number; last?: number; lastWall?: number } | null>(null);
  // The start-of-round countdown reads the SAME clock as the own worm, so GO (the
  // text and the beep) lands on the exact frame your worm starts moving. It used
  // to have its own un-slewed clock, which kept the numbers smooth but left it
  // about a one-way latency behind ownNow by the end of the countdown: your worm
  // set off that much before GO. The smoothness now comes from ownNow itself,
  // which only ever drifts gently (START_SLEW) while the round hasn't started.
  const countdownNow = () => ownNow();
  function ownNow(): number {
    const c = ownClockRef.current;
    const server = remoteRef.current!.getServerTime() + oneWayRef.current;
    if (!c) return server;
    const wall = performance.now();
    const sinceLast = c.lastWall === undefined ? 0 : Math.min(0.1, (wall - c.lastWall) / 1000); // a stalled frame doesn't buy a big step
    c.lastWall = wall;
    const elapsed = (wall - c.wall0) / 1000;
    let t = c.t0 + elapsed;
    const drift = server - t; // > 0 = server ahead of us, < 0 = we're ahead
    if (Math.abs(drift) > 1.5) {
      // A huge drift (e.g. the tab was backgrounded): re-anchor to the server now. This
      // only fires while nothing is being drawn, so the jump is invisible.
      c.t0 = server - elapsed;
      t = c.t0 + elapsed;
    } else if (t < ((remoteRef.current!.getGameState() as any)?.startPhaseTime ?? 0)) {
      // Start phase: nothing moves yet, the countdown is what shows this clock. Close
      // the drift at a capped RATE of wall time (so it's the same however many times
      // a frame this is called): the numbers run at most START_SLEW fast or slow,
      // which is invisible, and the clock has settled on the server well before GO.
      const cap = START_SLEW * sinceLast;
      c.t0 += Math.max(-cap, Math.min(cap, drift));
      t = c.t0 + elapsed;
    } else if (Math.abs(drift) > 0.02) {
      // Gently slew toward the server estimate in BOTH directions - never SNAP. On a
      // heavy map the server sim stalls then delivers updates bunched, which used to snap
      // this clock (and thus the own-worm head) backward, then forward: the "jolt". A slew
      // instead shows up as your worm briefly slowing / speeding, not teleporting.
      c.t0 += drift * 0.03;
      t = c.t0 + elapsed;
    }
    // Hard monotonic guard: the own worm is client-authoritative and its predicted path
    // only ever grows forward, so the render target - and with it the drawn head - must
    // NEVER move backward. This is the actual guarantee that kills the visible jolt.
    if (c.last !== undefined && t < c.last) t = c.last;
    c.last = t;
    return t;
  }
  // Lobby/waiting-room state (shared rooms): roster + when the match starts.
  // hostPlayerId is set only for custom lobbies (so we know who may start it).
  const lobbyRef = useRef<{ players: any[]; startAt: number; etaAt: number; hostPlayerId: string | null; map: any; maxPlayers: number; botFill: boolean; botFillSkill: number | null; config: any; roomName?: string | null }>({ players: [], startAt: 0, etaAt: 0, hostPlayerId: null, map: null, maxPlayers: 0, botFill: false, botFillSkill: null, config: {}, roomName: null });
  const startGameRef = useRef<() => void>(() => {});
  // Lobby chat log (custom lobbies): appended as `chat` messages arrive, reported
  // up to the embedding lobby via onSession. sendChat emits a new line.
  const chatRef = useRef<ChatMessage[]>([]);
  const sendChatRef = useRef<(text: string) => void>(() => {});
  // Host edits the live lobby's settings / couch seats before starting.
  const updateConfigRef = useRef<(cfg: any) => void>(() => {});
  const setLocalsRef = useRef<(list: any[]) => void>(() => {});
  const addBotRef = useRef<(skill?: number) => void>(() => {});
  const kickBotRef = useRef<(id: string) => void>(() => {});
  const kickPlayerRef = useRef<(id: string) => void>(() => {});
  // Why the session ended, shown on the disconnected screen (room full / in progress).
  const endReasonRef = useRef<string | null>(null);
  // Rating movement from the server's rating_update (public boxes only).
  const ratingUpdatesRef = useRef<Record<string, { before: number; after: number; firstMatch?: boolean; placement?: { played: number; of: number } | null }> | null>(null);
  // Achievement levels each seat reached this match (achievement_update, a beat
  // after rating_update): the Match End modal celebrates the viewer's own.
  const unlocksRef = useRef<Record<string, Unlock[]> | null>(null);
  // ... and what the match moved for each seat (the same message): the small
  // cards in the corner after the match (AchievementProgress).
  const movesRef = useRef<Record<string, AchievementMove[]> | null>(null);
  const movesSeqRef = useRef(0); // bumped per achievement_update: the cards come in again
  // A new game: the match's XP tally starts over.
  useEffect(() => { resetMatchProgress(); }, []);
  // Which ladder the rating_update moved (casual, or a ranked mode): the Match
  // End modal names it.
  const ratingModeRef = useRef<string | null>(null);
  // A ranked room's seats with their ladder ratings (ranked_roster, a beat after
  // match_start): the match-found banner wears them. null until it lands.
  const rankedRosterRef = useRef<Record<string, { rating: number | null; games: number }> | null>(null);
  // Match end shows the modal first; its Continue opens the full screen.
  const [resultsOpen, setResultsOpen] = useState(false);
  // The changing rooms' results (Play Now's last set, the Royale so far or at
  // its end), opened from their screens over the game; the game runs on below.
  const [dynResults, setDynResults] = useState(false);
  // The Royale: where each player went out (their place and the stage), as the
  // stages end; the placings of the results screen before the Royale is over.
  const royaleOutRef = useRef<Record<string, { place: number; stage: number; final: boolean }>>({});
  // Play Now: the last set that ended (its standings, stats and players).
  const lastSetRef = useRef<{ set: number; standings: { id: string; score: number }[]; stats: any; players: any[] } | null>(null);
  // The finished match's replay: the host stores it at match end and says so
  // (replay_ready); its rounds then fill the results screen's "watch replay" row,
  // exactly like a local match's. watchRound = the round being watched, if any.
  const replayMatchIdRef = useRef<string | null>(null);
  const [replayRounds, setReplayRounds] = useState<any[] | null>(null);
  const [watchRound, setWatchRound] = useState<number | null>(null);
  const loadReplay = (matchId: string) => {
    replayMatchIdRef.current = matchId;
    fetchMatchReplay(matchId, serverOverride).then((res) => {
      if (!("error" in res) && replayMatchIdRef.current === matchId) setReplayRounds(res.match.matchState.roundsData);
    });
  };
  // A reload of a finished match's URL (signing in from the match-end modal via
  // an OAuth provider leaves the page and comes back here) restores the results
  // from the per-tab snapshot and never connects: there is nothing to rejoin.
  const restoredRef = useRef(false);
  const [restored] = useState(() => {
    const snap = loadMatchEndSnapshot();
    if (!snap) return false;
    matchConfigRef.current = snap.matchConfig;
    startScoreRef.current = snap.score;
    ratingUpdatesRef.current = snap.ratingUpdates;
    unlocksRef.current = snap.unlocks ?? null;
    matchStatsRef.current = snap.stats;
    localIdsRef.current = snap.localIds;
    replayMatchIdRef.current = snap.replayMatchId ?? null;
    restoredRef.current = true;
    return true;
  });
  useEffect(() => {
    if (restored && replayMatchIdRef.current) loadReplay(replayMatchIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restored]);
  useEffect(() => {
    if (restored) {
      setPhase("over");
      setResultsOpen(true);
    }
  }, [restored]);

  // Debug (?debug=1): expose live handles so instrumentation (e.g. input-latency
  // measurement from the console) can reach the streamed gameState and our ids.
  useEffect(() => {
    if (!debug) return;
    (window as any).__achtungOnline = {
      gs: () => remoteRef.current?.getGameState(),
      youIds: () => localIdsRef.current,
      ownNow,
    };
    return () => { delete (window as any).__achtungOnline; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debug]);

  // Fresh auth identity for the socket handlers: the join emit lives in the
  // mount-only effect below, so reading `user` there would freeze the value from
  // the first render — a player whose session resolves a beat after page load
  // would be seated (and rated) as a guest. The ref always holds the latest, and
  // when the session resolves while we're in a waiting room, we re-send
  // set_locals so the server upgrades the seat's identity (it refreshes
  // accountId on kept seats pre-start).
  const userRef = useRef(user);
  // The Supabase access token rides along so the server can VERIFY the claimed
  // accountId (and gate paid cosmetics by real ownership) instead of trusting it.
  const sessionRef = useRef(session);
  const identityResendRef = useRef<() => void>(() => {});
  useEffect(() => {
    userRef.current = user;
    sessionRef.current = session;
    identityResendRef.current();
  }, [user, session]);

  const onRoomInstanceRef = useRef(onRoomInstance);
  onRoomInstanceRef.current = onRoomInstance;

  useEffect(() => {
    if (restoredRef.current) return; // showing a finished match's results: nothing to connect to
    const remote = remoteRef.current!;
    const socket = connectToServer(serverOverride, room, (instance) => onRoomInstanceRef.current?.(instance));
    // One steering listener per local human, (re)bound each round to its keys.
    let steeringListeners: ReturnType<typeof PlayerSteeringListener>[] = [];
    // Local worms we've already told the server crashed (report once per round).
    const crashReported = new Set<string>();
    // Last predicted-pickup count per local worm, so we can play the grab SFX for a
    // victory-lap pickup: on the lap the server never sends the despawn event the audio
    // loop normally keys off (roundActive is false), so it stays silent otherwise.

    function sendSteering(playerId: string, s: number) {
      socket.emit("player_steering", { playerId, steering: s });
      // Client-authoritative: your worm follows your input immediately, no round trip.
      const p = predictorsRef.current[playerId];
      if (p) p.setSteering(s);
    }
    sendSteeringRef.current = sendSteering;
    // Lets the embedding lobby's "Start now" button start the box immediately.
    startNowRef.current = () => socket.emit("start_now");
    // Custom lobby: the host's "Start" button starts the match.
    startGameRef.current = () => socket.emit("start_game");
    // Custom lobby chat: send a message (server broadcasts it back to everyone).
    sendChatRef.current = (text: string) => { const t = (text || "").trim(); if (t) socket.emit("chat", { text: t }); };
    // Host: push edited settings / couch seats to the open lobby.
    updateConfigRef.current = (cfg: any) => socket.emit("lobby_config", cfg);
    // Seat 0 always carries the signed-in account id (null when signed out); other
    // couch seats on the same device stay guests. Applied to every set_locals so
    // lobby seat edits can never strip the identity the rating pipe needs.
    const withIdentity = (list: any[]) => list.map((p: any, i: number) => ({ ...p, accountId: i === 0 ? userRef.current?.id ?? null : null }));
    setLocalsRef.current = (list: any[]) => socket.emit("set_locals", { localPlayers: withIdentity(list), authToken: sessionRef.current?.access_token ?? null, tz: localTimeZone() });
    // Late sign-in heal: once the auth session resolves, refresh our seats so the
    // server re-tags seat 0 with the account id (no-op after match start).
    identityResendRef.current = () => {
      if (!socket.connected) return;
      socket.emit("set_locals", { localPlayers: withIdentity(localPlayersRef.current.map((p) => ({ name: p.name, colorId: p.colorId, flag: (p as any).flag, victoryAnim: (p as any).victoryAnim ?? null, headStyle: (p as any).headStyle ?? null }))), authToken: sessionRef.current?.access_token ?? null, tz: localTimeZone() });
    };
    addBotRef.current = (skill?: number) => socket.emit("add_bot", { skill });
    kickBotRef.current = (id: string) => socket.emit("remove_bot", { id });
    kickPlayerRef.current = (id: string) => socket.emit("kick", { id });

    // Measure round-trip latency periodically so the prediction clock tracks the
    // server. One-way latency is smoothed and capped to keep it stable.
    function measureRtt() {
      const t0 = Date.now();
      socket.timeout(2000).emit("rtt_ping", null, (err: any) => {
        if (err) return;
        const ow = Math.min(0.25, (Date.now() - t0) / 2000);
        oneWayRef.current = oneWayRef.current === 0 ? ow : oneWayRef.current * 0.6 + ow * 0.4;
      });
    }
    const rttTimer = setInterval(measureRtt, 2000);
    measureRtt();

    // Tell the host what we saw when one of our worms died (online/death-report.ts);
    // it keeps the report in the match's replay. Live rounds only: a lap crash is
    // cosmetic and disputes nothing.
    function sendDeathReport(playerId: string, wormId: any, calledBy: "client" | "server", serverEvent?: any) {
      try {
        const gs = remote.getGameState();
        const p = predictorsRef.current[playerId];
        if (!gs || !p) return;
        const track = (p.tracks ? p.tracks() : []).find((t: any) => wormId == null || t.wormId === wormId);
        const g = updGapRef.current;
        const nowP = performance.now();
        const report = buildDeathReport({
            gameState: gs, playerId, wormId, trackId: track ? track.trackId : null, calledBy, round: roundRef.current,
            local: p.crashInfo ? p.crashInfo(wormId) : null, serverEvent,
            clocks: { ownNow: ownNow(), serverNow: serverNow(), renderTime: remote.getRenderTime(), oneWay: oneWayRef.current, renderDelay: remote.getRenderDelay() },
            net: { maxUpdateGapMs: Math.max(0, ...g.gaps.slice(-90)), lastUpdateAgeMs: g.last > 0 ? nowP - g.last : 0 },
          });
        socket.emit("death_report", { playerId, report });
        // The deaths a player cannot explain, to the error reports too (shared/monitoring.ts):
        // our own collision hit trail that is not in the killer's path, or the host
        // ended a worm it had stopped hearing from while we were still driving it.
        const trace = report.local && report.local.trace;
        const about = { room: dynKind || (room || "").split("#")[0], round: report.round, clocks: report.clocks, net: report.net, visible: report.page.visible };
        if (calledBy === "client" && trace && trace.ghost) reportSignal("death: hit a trail that is not there", { ...about, local: report.local });
        // A hidden page (app in the background, another tab) has its timers held, so the
        // host going deaf to it is the designed outcome, not a fault (Sentry ACHTUNG-ROYALE-4).
        else if (calledBy === "server" && serverEvent && serverEvent.via === "silent_client" && report.page.visible !== false) reportSignal("death: host stopped hearing our worm", { ...about, head: report.head });
        else if (calledBy === "server" && report.local == null && report.clocks.aheadOfServer > 0.4) reportSignal("death: host called it far behind our view", { ...about, serverEvent: report.serverEvent, head: report.head });
      } catch { /* forensics only: never in the way of the game */ }
    }

    // Stream each owned worm's path (the client is authoritative for its movement)
    // so the server can validate it and run collision against it.
    const sendTimer = setInterval(() => {
      const gs = remote.getGameState();
      if (!gs) return;
      for (const id of localIdsRef.current) {
        const p = predictorsRef.current[id];
        if (!p) continue;
        // Register any twin we just spawned BEFORE streaming its segments (in-order on this
        // connection), so the host has the worm when its first segments arrive.
        const newWorms = p.drainNewWorms ? p.drainNewWorms() : [];
        for (const worm of newWorms) socket.emit("worm_spawn", { playerId: id, worm });
        const segments = p.drainSegments(gs);
        if (segments.length) socket.emit("worm_segments", { playerId: id, segments });
        // Victory-lap pickups sound like any other: the host simulates the lap and
        // streams the despawn event, which the audio loop counts (playing the grab
        // sound here as well made every lap pickup sound twice).
        // The client owns its worm(s) + detects each one's own crash (local collision).
        // Report EACH crashed worm by wormId (a player can control two after a twin), so
        // the server kills the right worm even if its own collision disagreed (dense
        // rounds). drainCrashes returns each newly-crashed wormId once.
        const crashed = p.drainCrashes ? p.drainCrashes() : (p.isDead && p.isDead() && !crashReported.has(id) ? (crashReported.add(id), [null]) : []);
        for (const wormId of crashed) {
          // Report every crash, on a live round AND on the post-win victory lap. A lap
          // crash is cosmetic (the round is already scored; the server's lap frames are
          // flagged so nobody is marked out), but reporting it lets the server emit the
          // worm_died / player_died events that drive the celebration's crash sequence
          // and the crash sound, for us and for everyone watching. The server ignores
          // a report that lands outside a round or a lap.
          // with what our collision saw (wall or whose trail), for the host to credit the
          // crash when its own view of the moment can't (a Respawn round scores it)
          const ci = p.crashInfo ? p.crashInfo(wormId) : null;
          socket.emit("worm_crashed", { playerId: id, wormId, cause: ci?.cause, killerTrackId: ci?.killer, cell: ci?.cell?.index });
          if (roundActiveRef.current) sendDeathReport(id, wormId, "client");
          // Optimistic own-death: mark THIS worm dead so it stops - but ONLY in a live
          // round. A victory-lap crash is cosmetic (the round is already scored), so
          // marking it out would grey the WINNER's name on the scoreboard. The worm still
          // freezes on the lap (its predictor stops once local collision reports dead).
          if (roundActiveRef.current) {
            const dw = gs.worms.find((w: any) => (wormId != null ? w.id === wormId : w.playerId === id));
            if (dw) dw.alive = false;
          }
        }
        // Player-level dead flag once ALL our worms have crashed (live rounds only, so a
        // lap wipeout never marks the winner out - see above).
        if (crashed.length && roundActiveRef.current && p.isDead && p.isDead()) {
          const dp = gs.players && gs.players.find((pl: any) => pl.id === id);
          if (dp) dp.alive = false;
        }
      }
    }, 33);

    socket.on("connect", () => {
      setPhase("connecting");
      socket.emit("play", {
        ...(options || {}),
        // Stable per-device id: lets the server hand this seat back after a reload
        // (reconnect into a running match) instead of bouncing the join.
        clientId: getClientId(),
        tabId: getTabId(),
        // We read the compact game_update format (core's WIRE_VERSION_COMPACT).
        wire: 3,
        botCount,
        // Name/colour of player 1 (kept for solo/back-compat); localPlayers carries
        // every local human on this device so the server seats them all in the room.
        name: localPlayersRef.current[0]?.name ?? name,
        colorId: localPlayersRef.current[0]?.colorId ?? colorId,
        // Only player 1 carries the signed-in account id (so the match is rated
        // under it); a second local human on the same device stays a guest. Read
        // through the ref: this closure is mount-frozen, the session may not be.
        localPlayers: localPlayersRef.current.map((p, i) => ({ name: p.name, colorId: p.colorId, flag: (p as any).flag, victoryAnim: (p as any).victoryAnim ?? null, headStyle: (p as any).headStyle ?? null, accountId: i === 0 ? userRef.current?.id ?? null : null })),
        // Proof for the accountId claim; the server treats unproven ids as guests.
        authToken: sessionRef.current?.access_token ?? null, tz: localTimeZone(),
        map: lobbyConfig ? (options?.map ?? map) : map,
        room,
        ...(benchFillTo != null ? { bench: { fillTo: benchFillTo, powerUps: search.get("benchpu") === "1" } } : {}),
        // Custom-lobby host config (server only honors the first/host join).
        ...(lobbyConfig || {}),
      });
    });

    // Shared-room lobby: roster + countdown while waiting for the match to start.
    socket.on("lobby_update", (m: any) => {
      lobbyRef.current = { players: m.players || [], startAt: m.startAt || 0, etaAt: m.etaAt || 0, hostPlayerId: m.hostPlayerId ?? null, map: m.map ?? null, maxPlayers: m.maxPlayers || 0, botFill: !!m.botFill, botFillSkill: m.botFillSkill ?? null, roomName: typeof m.roomName === "string" ? m.roomName : null, config: { map: m.map ?? null, maxScore: m.maxScore ?? null, maxRounds: m.maxRounds ?? null, disabledPowerUps: m.disabledPowerUps ?? [], powerUpSpawnChance: m.powerUpSpawnChance ?? null, wormSpeed: m.wormSpeed ?? null, respawn: !!m.respawn } };
      setPhase((cur) => (cur === "playing" ? cur : "waiting"));
      forceRender();
      bumpSession();
    });
    // Bounced from a lobby (can't be here): leave to the home screen with a toast,
    // instead of a dead-end screen. Disconnect first so the socket doesn't auto-
    // reconnect and silently rejoin the same room (the URL still points at it).
    function boot(reason: string) {
      showToast(reason);
      try { socket.disconnect(); } catch { /* already closed */ }
      leaveSession();
    }
    socket.on("room_unavailable", () => {
      // On the way back into a remembered game (online/resume.ts) that is gone by
      // now: quietly home, as a reload would; never matchmake a fresh room.
      if (isResuming()) {
        endResuming();
        forgetGame(resumeKind || undefined);
        clearStickyRoom(room);
        try { socket.disconnect(); } catch { /* already closed */ }
        onLeaveRef.current();
        return;
      }
      // A changing room that is full or just closed: the matchmaker has another.
      if (dynKind && dynRetriesRef.current < 3) {
        dynRetriesRef.current++;
        refuseStickyRoom(room);
        try { socket.disconnect(); } catch { /* already closed */ }
        setConnectEpoch((e) => e + 1);
        return;
      }
      // Bounced by a room we thought was ours: the sticky instance, or a concrete
      // match-id URL, names a match that is finished or that we hold no seat in.
      // A browser RELOAD into that lands quietly on the home screen - the match the
      // player was in no longer exists, so there is nothing to rejoin. A deliberate
      // visit with a stale STICKY (opening /online again later in the same tab)
      // drops it and reconnects once, matchmaking a fresh room. Anything else (a
      // shared link to a dead match, a room we never sat in) boots with a toast.
      const concrete = !!room && room.indexOf("#") !== -1;
      if ((hasStickyRoom(room) || concrete) && !retriedStickyRef.current) {
        retriedStickyRef.current = true;
        clearStickyRoom(room);
        try { socket.disconnect(); } catch { /* already closed */ }
        if (NAV_IS_RELOAD) {
          onLeaveRef.current();
          return;
        }
        if (!concrete) {
          setConnectEpoch((e) => e + 1);
          return;
        }
      }
      boot("That match is no longer available.");
    });
    socket.on("lobby_full", () => boot("That lobby is full."));
    // A ranked box seats one signed-in player per device.
    socket.on("ranked_locked", (m: any) => boot(m && m.reason === "solo" ? "Ranked seats one player per device." : "Sign in to play ranked."));
    socket.on("ranked_roster", (m: any) => {
      if (m && m.ratings) { rankedRosterRef.current = m.ratings; bumpSession(); }
    });
    socket.on("lobby_closed", () => boot("The host closed the lobby."));
    // This device joined the room again (another tab, or after a restart): the seat went there.
    socket.on("replaced", () => boot("You joined this game somewhere else."));
    // Play Now: no steering for a few rounds in a row, so the seat went to someone else.
    socket.on("idle_out", () => boot("Kicked because of inactivity"));
    socket.on("kicked", () => boot("The host removed you from the lobby."));

    // Lobby chat line (player message or "X joined/left" system line).
    socket.on("chat", (m: any) => {
      if (!m || typeof m.text !== "string") return;
      chatRef.current = [...chatRef.current, { name: m.name, colorId: m.colorId ?? null, text: m.text, system: !!m.system }].slice(-200);
      bumpSession();
    });

    // The seats this client drives right now (each gets its own predictor).
    function setActiveIds(ids: string[]) {
      localIdsRef.current = ids;
      if (predict) {
        const map: Record<string, any> = {};
        // Collide our head only against opponent trail we can actually see (the
        // render clock), so we don't die in the open against undrawn trail.
        ids.forEach((id) => { map[id] = createOwnWormController(id, { getOpponentClipTime: () => remote.getRenderTime() }); });
        predictorsRef.current = map;
        remote.setLocalPlayerIds(ids);
      }
    }
    socket.on("assigned", (m: any) => {
      // The server assigns one playerId per local human on this device.
      const ids: string[] = m.playerIds || (m.playerId ? [m.playerId] : []);
      seatIdsRef.current = ids;
      // `pending`: we dropped into a running room and play from the next round.
      pendingRef.current = !!m.pending;
      setActiveIds(m.pending ? [] : ids);
      bumpSession();
    });

    socket.on("match_start", (m: any) => {
      matchConfigRef.current = m.matchConfig;
      startScoreRef.current = scoreUtil.getStartScore(m.matchConfig.players);
      roundStartScoreRef.current = startScoreRef.current;
      roundRef.current = 0;
      (m.matchConfig.players || []).forEach((p: any) => { knownPlayersRef.current[p.id] = p; });
    });

    // A changing room's round: a new roster, map and rules (online/dynamic.ts).
    socket.on("round_setup", (m: any) => {
      if (!m || !m.matchConfig) return;
      setupRef.current = m;
      // The Royale's final, the 1 vs 1, is played in the gold arena (the next
      // round's map is drawn with it: the renderer reads this when it is made).
      setMatchBoardSkin(m.dynamic === "royale" && m.final ? "gold" : null);
      if (m.openingMs > 0) openingAtRef.current = Date.now(); // the Royale's opening starts now
      matchConfigRef.current = m.matchConfig;
      const ps: any[] = m.matchConfig.players || [];
      ps.forEach((p) => { knownPlayersRef.current[p.id] = p; });
      // The score sheet covers this round's roster: newcomers start at 0, whoever
      // left is gone, and a new set (or the Royale's final) starts from 0.
      const base = m.scoreReset ? {} : (startScoreRef.current || {});
      const sheet: Record<string, number> = {};
      ps.forEach((p) => { sheet[p.id] = base[p.id] || 0; });
      startScoreRef.current = sheet;
      roundStartScoreRef.current = sheet;
      if (m.scoreReset) roundWinnersRef.current = [];
      const r = m.dynamic === "playnow" ? m.setRound : m.royaleRound;
      if (typeof r === "number") roundRef.current = r - 1; // +1 at game_start, like every round
      // Which of our seats play this round (none while watching).
      const active = seatIdsRef.current.filter((id) => ps.some((p) => p.id === id));
      if (active.length) pendingRef.current = false;
      if (active.join() !== localIdsRef.current.join()) setActiveIds(active);
      bumpSession();
      forceRender();
    });
    // Someone joined the running round (Play Now's Respawn rounds put a newcomer in
    // at once): their name and colour, to draw them in, and a line on the score
    // sheet. Nothing else about the round changes.
    socket.on("roster_add", (m: any) => {
      const cfg = matchConfigRef.current;
      if (!cfg || !m || !Array.isArray(m.players)) return;
      const add = m.players.filter((p: any) => p && p.id && !(cfg.players || []).some((x: any) => x.id === p.id));
      if (!add.length) return;
      matchConfigRef.current = { ...cfg, players: [...(cfg.players || []), ...add] };
      if (setupRef.current) setupRef.current = { ...setupRef.current, matchConfig: matchConfigRef.current } as RoundSetup;
      add.forEach((p: any) => {
        knownPlayersRef.current[p.id] = p;
        if (startScoreRef.current) startScoreRef.current = { ...startScoreRef.current, [p.id]: 0 };
        if (roundStartScoreRef.current) roundStartScoreRef.current = { ...roundStartScoreRef.current, [p.id]: 0 };
      });
      forceRender();
    });
    // The Royale kept our result: under our account (the reward screen can read
    // the new tally now), or as a token a guest may claim after signing in.
    socket.on("royale_recorded", () => { royaleSavedRef.current = royaleSavedRef.current + 1; forceRender(); });
    socket.on("royale_claim", (m: any) => { if (m && typeof m.token === "string") keepGuestRoyaleResult(m.token, m.place | 0); });
    // A changing room closed under us (nobody left in it, or it handed us on).
    socket.on("room_closed", () => {
      if (!dynKind || movedRef.current || phaseRef.current === "over") return;
      boot("That game has ended.");
    });

    socket.on("game_start", (m: any) => {
      clearMatchEndSnapshot(); // a new match on this URL: the old results must not hijack a reload
      remote.setInitial(m.gameState);
      // Reload forensics: on a resume, dump the state the predictor will continue
      // from (the own track's tail + the worm's flags), so a death moments later
      // can be correlated with the exact seam state.
      if (m.resumed && m.gameState) {
        try {
          for (const w of m.gameState.worms || []) {
            if (!localIdsRef.current.includes(w.playerId)) continue;
            const segs = (m.gameState.wormPathSegments || {})[w.trackId] || [];
            const last = segs[segs.length - 1];
            console.log("[resume]", JSON.stringify({
              t: m.gameState.gameTime, wormId: w.id, alive: w.alive, ext: w.externallyControlled,
              dist: w.distanceTravelled, x: w.centerX, y: w.centerY, segCount: segs.length,
              lastSeg: last && {
                type: last.type, startTime: last.startTime, endTime: last.endTime,
                duration: last.duration, speed: last.speed, jump: !!last.jump,
                endX: last.endX, endY: last.endY,
              },
            }));
          }
        } catch { /* log only */ }
      }
      // Reconnect resume: the server replays the join sequence with the LIVE state, and
      // its game_start then carries the settled score so far and the real 1-based round
      // number (a normal round start carries neither). Adopt them so the scoreboard and
      // round counter don't restart from zero mid-match.
      if (m.score) startScoreRef.current = m.score;
      if (typeof m.round === "number" && !setupRef.current) roundRef.current = m.round - 1; // +1 below, like every round
      // (A changing room: the sheet covers the round's roster, and the round number
      // came with its round_setup.)
      if (m.score && setupRef.current) {
        const sheet: Record<string, number> = {};
        (matchConfigRef.current?.players || []).forEach((p: any) => { sheet[p.id] = m.score[p.id] || 0; });
        startScoreRef.current = sheet;
      }
      nextRef.current = null;
      roundResultRef.current = null;
      roundStartScoreRef.current = startScoreRef.current;
      introScheduleRef.current = introSchedule(matchConfigRef.current?.players || [], startScoreRef.current, m.gameState);
      introDelaysRef.current = introScheduleRef.current.delays;
      roundOverScoreRef.current = null; // new round: back to the live running score
      // Anchor the own-worm local clock (the countdown reads it too) to this round's
      // start time. The message took a one-way trip to get here, so the server is
      // already that far past its gameTime: start there and the clock has (next to)
      // nothing left to close during the countdown.
      const t0 = ((m.gameState && m.gameState.gameTime) || 0) + oneWayRef.current;
      const wall0 = performance.now();
      ownClockRef.current = { t0, wall0 };
      // Own a fresh path for each local worm this round, on this round's state only.
      const roundGs = remote.getGameState();
      Object.values(predictorsRef.current).forEach((p: any) => p.reset(roundGs));
      crashReported.clear();
      roundRef.current += 1;
      roundActiveRef.current = true;
      // Once per match, not once per round: this block runs on every round start.
      if (roundRef.current === 1) {
        track("Game Started", { mode: "online" });
        noteMatchStarted(); // the first-run hints' count (first-run.ts)
      }
      setPhase("playing");
      forceRender();
      // (Re)bind keyboard steering: one listener per local human, each on its own
      // keys (player 1 defaults to arrows; player 2 uses its configured pair).
      steeringListeners.forEach((s) => s.removeKeyListeners());
      steeringListeners = [];
      localIdsRef.current.forEach((id, i) => {
        const lp: Partial<LocalEntry> = localPlayersRef.current[i] || {};
        const left = (lp.left ?? keyPairs[i]?.left ?? keyPairs[0].left) as string;
        const right = (lp.right ?? keyPairs[i]?.right ?? keyPairs[0].right) as string;
        const listener = PlayerSteeringListener();
        listener.addKeyListeners({ left, right, onSteeringUpdate: (s: number) => sendSteering(id, s) });
        steeringListeners.push(listener);
      });
    });

    // Deltas mutate the gameState in place; no React re-render so the canvas's own
    // animation loop just picks up the new trajectory segments on its next frame.
    socket.on("game_update", (m: any) => {
      // Debug: track how evenly the server's updates arrive. A bunched stream (one
      // long gap, then several at once) is what makes the clocks slew and the game
      // feel stuttery even at a solid fps, so the overlay surfaces the worst gap.
      const nowP = performance.now();
      if (m.data && typeof m.data.gameTime === "number") {
        const hp = hostPaceRef.current;
        hp.push({ wall: nowP, gameTime: m.data.gameTime, ticks: m.data.perf ? m.data.perf.ticks || 0 : 0, sentAt: typeof m.data.sentAt === "number" ? m.data.sentAt : 0 });
        while (hp.length > 2 && nowP - hp[0].wall > 10000) hp.shift();
      }
      const g = updGapRef.current;
      if (g.last > 0) {
        const gap = nowP - g.last;
        if (gap > g.maxGap) g.maxGap = gap;
        g.gaps.push(gap);
        if (g.gaps.length > 300) g.gaps.shift(); // ~10s of history at typical cadence
      }
      g.last = nowP;
      // Death forensics (reload investigation): surface every worm death the server
      // streams, with its debug payload (cause, killer, fatal cell + immunity
      // stamps, segment timing, client-reported or host-detected).
      if (m.data && Array.isArray(m.data.gameEvents)) {
        for (const ev of m.data.gameEvents) {
          if (ev && ev.type === "worm_died") {
            const own = localIdsRef.current.includes(ev.playerId);
            console.log("[death:server]" + (own ? " (YOU)" : ""), JSON.stringify(ev));
            // The HOST called our death (not an echo of our own crash report): say what
            // we saw, now, BEFORE applyUpdate snaps our predicted path back to its verdict.
            if (own && ev.via !== "client_report" && roundActiveRef.current && !m.data.victoryLap) sendDeathReport(ev.playerId, ev.wormId, "server", ev);
          }
        }
      }
      remote.applyUpdate(m.data);
    });

    // Switcharoonie: the host schedules a synchronized freeze -> swap -> go. Forward the timing
    // to every local predictor (each holds its own worm(s), swaps + re-binds at go), and stash the
    // animation info on the shared gameState so the swap effect renders (any worm-count combo).
    socket.on("switch_freeze", (m: any) => {
      if (!m) return;
      Object.values(predictorsRef.current).forEach((p: any) => p.switchFreeze && p.switchFreeze(m.atTime, m.assignments));
      const gs = remote.getGameState();
      if (gs) gs.switchAnim = { freezeTime: m.atTime, goTime: null, assignments: m.assignments };
    });
    socket.on("switch_go", (m: any) => {
      if (!m) return;
      Object.values(predictorsRef.current).forEach((p: any) => p.switchGo && p.switchGo(m.atTime));
      const gs = remote.getGameState();
      if (gs && gs.switchAnim) gs.switchAnim.goTime = m.atTime;
    });

    // The host rejected a twin we spawned (a contested grab we lost): drop it locally.
    // removeWorm is idempotent, so just offer it to each of our predictors.
    socket.on("worm_rejected", (m: any) => {
      const gs = remote.getGameState();
      if (!gs || m == null || m.wormId == null) return;
      for (const id of localIdsRef.current) {
        const p = predictorsRef.current[id];
        if (p && p.removeWorm) p.removeWorm(gs, m.wormId);
      }
    });

    socket.on("game_over", (m: any) => {
      roundActiveRef.current = false; // round scored: a victory-lap crash stays local
      const gs = remote.getGameState();
      const players = matchConfigRef.current?.players || [];
      if (gs) {
        // Round winner = top of this round's score (longest survivor), like local.
        const sorted = scoreUtil.createSortedList(scoreUtil.calculateRoundScore(gs));
        roundWinnerRef.current = players.find((p: any) => p.id === sorted[0]?.id) || null;
        roundWinnerPointsRef.current = sorted[0] ? sorted[0].score : null;
        // Prefer the server's authoritative accumulated score (through this round) so
        // the total is consistent across clients and doesn't snap as death events
        // finish propagating. Fall back to a local recompute if it wasn't sent.
        roundBeforeScoreRef.current = startScoreRef.current;
        startScoreRef.current = m && m.score ? m.score : scoreUtil.combineScores(startScoreRef.current, scoreUtil.calculateRoundScore(gs));
        // Show that settled total directly until the next round starts.
        roundOverScoreRef.current = startScoreRef.current;
        if (roundWinnerRef.current && (roundWinnerPointsRef.current || 0) > 0) roundWinnersRef.current = [...roundWinnersRef.current, roundWinnerRef.current.id];
        // The round-over sting, or our own win's instead, a beat after the last
        // crash so the two do not run together.
        const wonIt = !!roundWinnerRef.current && (roundWinnerPointsRef.current || 0) > 0 && localIdsRef.current.includes(roundWinnerRef.current.id);
        if (wonIt) musicController.play("round-win", ROUND_STING_DELAY);
        else musicController.playRoundEnd(ROUND_STING_DELAY);
        // A changing room says what the round meant: who is out of the Royale, a
        // Play Now set's podium. Those have their own screens (DynamicLayer), so the
        // ordinary round-over card stays away for them.
        if (dynKind) {
          const res: RoundResult = { setOver: !!m.setOver, standings: m.standings, royale: !!m.royale, out: m.out, places: m.places, alive: m.alive, winner: m.winner, stageOver: !!m.stageOver };
          roundResultRef.current = res;
          const mine = seatIdsRef.current.find((id) => res.places && res.places[id] != null);
          if (mine && res.places) myPlaceRef.current = res.places[mine];
          if (res.places) {
            const su = setupRef.current;
            for (const [id, place] of Object.entries(res.places)) royaleOutRef.current[id] = { place: place as number, stage: (su && su.stageNo) || 1, final: !!(su && su.final) };
          }
          if (res.setOver && res.standings) {
            lastSetRef.current = { set: (setupRef.current && setupRef.current.set) || 1, standings: res.standings, stats: m.stats || null, players: res.standings.map((s: any) => knownPlayersRef.current[s.id]).filter(Boolean) };
          }
          // knocked out of the Royale: nothing to come back to after a restart
          if (mine && res.places) forgetGame("royale");
          // (One banner says both who took the round and what comes next: the
          // layer's, so the ordinary round-over card never shows in these rooms.)
          roundWinnerDynRef.current = roundWinnerRef.current;
          roundWinnerRef.current = null;
        }
        // Match winner so far = top of the accumulated match score.
        const sortedMatch = scoreUtil.createSortedList(startScoreRef.current);
        matchWinnerRef.current = players.find((p: any) => p.id === sortedMatch[0]?.id) || null;
        forceRender();
      } else musicController.playRoundEnd(ROUND_STING_DELAY);
    });

    socket.on("game_countdown", (m: any) => {
      nextRoundAtRef.current = Date.now() + (m && m.duration ? m.duration : 5000);
      countdownSecsRef.current = (m && m.duration ? m.duration : 5000) / 1000;
      finalCountdownRef.current = !!(m && m.final);
      nextRef.current = (m && m.next) || null;
      pauseAtRef.current = Date.now();
      setPhase("countdown");
      forceRender();
    });

    // The results, kept per tab (see match-end-snapshot.ts). Saved at match end
    // and again when the ratings land, since they arrive after it.
    const snapshotEnd = () => saveMatchEndSnapshot({
      matchConfig: matchConfigRef.current,
      score: startScoreRef.current || {},
      ratingUpdates: ratingUpdatesRef.current,
      unlocks: unlocksRef.current,
      stats: matchStatsRef.current,
      localIds: localIdsRef.current,
      replayMatchId: replayMatchIdRef.current,
    });
    let matchEnded = false;
    // Once the match is over, hang up after a short linger (long enough for the
    // rating_update + replay_ready that follow match_over). The room takes no more
    // input, and a results screen left open would otherwise hold the room's DO
    // awake, and billed, for as long as the tab stays open.
    let postMatchTimer: ReturnType<typeof setTimeout> | null = null;
    let hungUp = false;

    socket.on("match_over", (m: any) => {
      // The server ships the whole match's fun-stat aggregate with the final
      // score; the Full Results screen renders yours in the "Match stats" box.
      if (m && m.stats) matchStatsRef.current = m.stats;
      // Players who walked out: the results rank them last (as the rating does).
      leftIdsRef.current = Array.isArray(m && m.left) ? m.left : [];
      // Play Now: the room is handing its players on (a set break during a deploy).
      // Rejoin through the matchmaker, which has a room for us.
      if (m && m.moved && dynKind === "playnow") {
        movedRef.current = true;
        forceRender();
        setTimeout(() => playAgain(), 1800);
        return;
      }
      if (m && m.royale) {
        royaleResultRef.current = m.royale;
        const i = (m.royale.placements || []).findIndex((id: string) => seatIdsRef.current.includes(id));
        if (i >= 0) myPlaceRef.current = i + 1;
      }
      track("Match Finished", { mode: dynKind || "online", rounds: roundRef.current });
      matchEnded = true;
      forgetGame(resumeKind || undefined);
      // (A changing room keeps no results for a reload: there is no results screen.)
      if (!dynKind) snapshotEnd();
      setPhase("over");
      forceRender();
      if (!postMatchTimer) {
        postMatchTimer = setTimeout(() => {
          hungUp = true;
          clearInterval(rttTimer);
          socket.disconnect();
        }, POST_MATCH_LINGER_MS);
      }
    });

    // The match's rating movement (arrives shortly after match_over, once the
    // server has persisted): { playerId: { before, after, firstMatch } }.
    // The host has stored this match's replay: fetch its rounds for the results screen.
    socket.on("replay_ready", (m: any) => {
      if (!m || typeof m.matchId !== "string") return;
      loadReplay(m.matchId);
      if (matchEnded) snapshotEnd();
    });

    // The worm level and the daily challenges, after every round (tallied for
    // the match's end modal: MatchXp).
    socket.on("progress_update", (m: any) => takeProgressUpdate(m));
    socket.on("progress_guest", (m: any) => { if (m && typeof m.token === "string") takeGuestRound(m.token); });

    socket.on("rating_update", (m: any) => {
      if (m && m.ratings) ratingUpdatesRef.current = m.ratings;
      if (m && typeof m.mode === "string") ratingModeRef.current = m.mode;
      if (matchEnded) snapshotEnd();
      forceRender();
    });

    socket.on("achievement_update", (m: any) => {
      if (m && m.unlocks && typeof m.unlocks === "object") unlocksRef.current = m.unlocks;
      if (m && m.moves && typeof m.moves === "object") movesRef.current = m.moves;
      movesSeqRef.current += 1;
      if (m && m.unlocks && Object.keys(m.unlocks).length) void refreshUnopened();
      if (matchEnded) snapshotEnd();
      forceRender();
    });

    socket.on("disconnect", () => {
      if (hungUp) return; // our own post-match hang-up: stay on the results
      setPhase("disconnected");
      forceRender();
    });

    return () => {
      clearInterval(rttTimer);
      clearInterval(sendTimer);
      if (postMatchTimer) clearTimeout(postMatchTimer);
      steeringListeners.forEach((s) => s.removeKeyListeners());
      socket.removeAllListeners();
      socket.disconnect();
    };
    // connectEpoch: bumped to rebuild the connection after dropping a stale sticky room.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectEpoch]);

  // Admin test aid: spawn the requested wiggle "humans" into this same room. Each is
  // an independent client-authoritative connection (see wiggle-peer.ts), torn down
  // when we leave.
  useEffect(() => {
    if (wiggleCount <= 0) return;
    const stops: Array<() => void> = [];
    for (let i = 0; i < wiggleCount; i++) {
      stops.push(createWigglePeer({ room, serverOverride, name: "Practice", colorId: "blue", lagMs: wiggleLagMs, jitterMs: wiggleJitterMs, jumps: wiggleJumps }));
    }
    return () => stops.forEach((s) => s());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feed the live server-streamed gameState to the music controller so online play
  // gets the same countdown beeps, crash sound and adaptive music as local play.
  // App's audibleView only drives music on the /play route, and online runs at
  // /online (or embedded over the lobby), so we own playback here for our lifetime.
  const audioProviderRef = useRef<(() => any) | null>(null);
  if (!audioProviderRef.current) {
    audioProviderRef.current = () => {
      const gs = remoteRef.current?.getGameState();
      const audio = computeGameAudio(gs, localIdsRef.current);
      // Align the 3-2-1-GO beeps with the OWN worm's clock (ownNow), which is what its
      // movement is predicted on, instead of the server's stepped gameTime. Online the
      // two differ by the latency + render delay, so keying the GO tick off gameTime made
      // it land after the worm already started; on localhost they coincide (perfect).
      if (audio && gs && typeof gs.startPhaseTime === "number") audio.countdownLeft = gs.startPhaseTime - countdownNow();
      return audio;
    };
  }
  // The controller sounds ONE game. Two can be mounted at once (the Royale you
  // wait for and the Play Now game you pass the time in): the one whose round is
  // live takes the sound, and one that leaves only lets go of its own. (Before,
  // the Play Now game unmounting when the Royale started silenced the Royale:
  // no countdown beeps, no crashes, no pickups.)
  useEffect(() => {
    if (phase === "playing" || phase === "countdown") musicController.setGameAudioProvider(audioProviderRef.current);
  }, [phase]);
  useEffect(() => {
    return () => {
      // (the music too: only the game that had the sound turns it off)
      if (audioProviderRef.current && musicController.releaseGameAudioProvider(audioProviderRef.current)) { musicController.setPlaying(false); musicController.setDucked(false); }
      setMatchBoardSkin(null); // the final's gold arena does not follow you out of the game
    };
  }, []);

  // Music plays while a round is live (it swells in over the start countdown),
  // carries on at half volume between rounds, and fades out when the match ends.
  useEffect(() => {
    // (a game that does not have the sound must not stop another one's music)
    if (phase === "playing" || (audioProviderRef.current && musicController.holdsGameAudioProvider(audioProviderRef.current))) {
      // (ducked from the results on; the controller lifts it at the countdown's "3")
      if (phase === "countdown") musicController.setDucked(true);
      if (phase === "playing") musicController.armUnduck();
      musicController.setPlaying(phase === "playing" || phase === "countdown");
      if (phase !== "playing" && phase !== "countdown") musicController.setDucked(false);
    }
  }, [phase]);

  // While in the waiting room, tick so the countdown updates on screen.
  useEffect(() => {
    if (phase !== "waiting") return;
    const t = setInterval(forceRender, 300);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Between rounds, tick so the "next round in N" countdown updates on screen.
  useEffect(() => {
    if (phase !== "countdown") return;
    const t = setInterval(forceRender, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Embedded mode: push waiting-room state up to the lobby (which renders the
  // modal). Fires on phase changes and roster/id updates (sessionTick).
  useEffect(() => {
    const cb = onSessionRef.current;
    if (!cb) return;
    const hostId = lobbyRef.current.hostPlayerId;
    cb({
      phase,
      players: lobbyRef.current.players,
      startAt: lobbyRef.current.startAt,
      etaAt: lobbyRef.current.etaAt,
      youIds: localIdsRef.current,
      rankedRatings: rankedRosterRef.current,
      startNow: () => startNowRef.current(),
      isHost: !!hostId && localIdsRef.current.includes(hostId),
      start: () => startGameRef.current(),
      code: lobbyCode,
      chat: chatRef.current,
      sendChat: (t: string) => sendChatRef.current(t),
      map: lobbyRef.current.map,
      maxPlayers: lobbyRef.current.maxPlayers,
      roomName: lobbyRef.current.roomName ?? null,
      botFill: lobbyRef.current.botFill,
      config: lobbyRef.current.config,
      botFillSkill: lobbyRef.current.botFillSkill ?? undefined,
      updateConfig: (cfg: any) => updateConfigRef.current(cfg),
      setLocals: (list: any[]) => setLocalsRef.current(list),
      addBot: (skill?: number) => addBotRef.current(skill),
      kickBot: (id: string) => kickBotRef.current(id),
      kickPlayer: (id: string) => kickPlayerRef.current(id),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sessionTick]);

  // Embedded over the lobby: while connecting/waiting, render nothing here - the
  // lobby draws the waiting modal from the session state we report via onSession.
  // The Royale's opening: everyone introduced, alone on the screen, while the
  // server holds round 1 back (setup.openingMs). It outlives the wait by its
  // fade, so it is part of whatever this renders until then.
  const openSetup = dynKind === "royale" && openingAtRef.current && setupRef.current && setupRef.current.openingMs ? setupRef.current : null;
  const royaleOpening = openSetup ? (
    <RoyaleOpening players={((openSetup as any).matchConfig?.players || []) as any} mine={seatIdsRef.current} at={openingAtRef.current} ms={openSetup.openingMs} rounds={openSetup.stageRounds || 3} out={openSetup.out || 0} seed={(openSetup as any).number || 0} mode={openSetup.mode} />
  ) : null;
  if (embedded && (phase === "connecting" || phase === "waiting")) {
    return royaleOpening;
  }

  // Waiting room (shared lobby, standalone /online route): roster + countdown.
  if (phase === "waiting") {
    const secs = Math.max(0, Math.ceil((lobbyRef.current.startAt - Date.now()) / 1000));
    const ps = lobbyRef.current.players;
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.75rem", minHeight: "60vh", textAlign: "center" }}>
        <h2>Waiting room{room ? ` · ${room}` : ""}</h2>
        <p style={{ fontSize: "20px" }}>Starting in {secs}s</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {ps.map((p: any) => (
            <div key={p.id} style={{ color: wormColors[p.colorId] || "#fff", fontWeight: 700 }}>
              {p.name}{localIdsRef.current.includes(p.id) ? " (you)" : ""}
            </div>
          ))}
        </div>
        <p style={{ opacity: 0.6, fontSize: "13px", maxWidth: 320 }}>
          Open this same link (same <code>?room=</code>) on another device to play together.
        </p>
        <button className="app-btn app-btn-ghost" onClick={leaveSession}>Leave</button>
      </div>
    );
  }

  const remote = remoteRef.current!;
  const gameState = remote.getGameState();
  const matchConfig = matchConfigRef.current;

  // Watching one of the finished match's rounds (from the results screen below).
  if (phase === "over" && matchConfig && watchRound !== null && replayRounds) {
    return <MatchReplay match={{ matchConfig, matchState: { roundsData: replayRounds } }} initialRound={watchRound} onExit={() => setWatchRound(null)} youIds={localIdsRef.current} />;
  }

  // Match over: the same full results screen as local play (podium + standings).
  // The rounds for its "watch replay" row come from the stored replay, a moment
  // after the match ends (empty until then, and if storing failed). A lightweight
  // match-like object feeds the shared component.
  // Rendered bare (no extra wrapper) so it sits directly in the route's
  // .page-main-game, exactly like the local match-over flow: same centering,
  // max-width and padding, so the header + buttons line up identically.
  if (phase === "over" && matchConfig && !dynKind && (resultsOpen || !rated)) {
    const fakeMatch: any = {
      getCurrentScore: () => startScoreRef.current,
      matchConfig,
      matchState: { roundsData: replayRounds || [] },
      isMatchOver: () => true,
    };
    const updates = ratingUpdatesRef.current;
    const playerRatings = updates
      ? Object.fromEntries(Object.entries(updates).map(([pid, u]) => [pid, { value: u.after, delta: u.after - u.before }]))
      : undefined;
    return (
      <MatchOver
        sound={!rated}
        match={fakeMatch}
        onRoundClick={(i: number) => setWatchRound(i)}
        onExitAction={leaveSession}
        onPlayAgain={onPlayAgain ? playAgain : undefined}
        leftIds={leftIdsRef.current}
        playerRatings={playerRatings}
        matchStats={matchStatsRef.current}
        statsPlayerIds={localIdsRef.current}
        mainPlayerId={localIdsRef.current[0]}
      />
    );
  }

  // Connecting / disconnected / before the first round: a simple status screen.
  if (!gameState || !matchConfig || phase === "disconnected") {
    const message =
      phase === "disconnected"
        ? (endReasonRef.current || "Disconnected from the server")
        : "Connecting to the game server...";
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          minHeight: "60vh",
          textAlign: "center",
        }}
      >
        <p>{message}</p>
        <button className="app-btn app-btn-primary" onClick={leaveSession}>
          Back to lobby
        </button>
        <p style={{ opacity: 0.6, fontSize: "12px", fontFamily: "monospace" }}>
          v{GAME_VERSION} · build {BUILD_LABEL}
        </p>
      </div>
    );
  }

  const players = matchConfig.players;
  const humans = players.filter((p: any) => localIdsRef.current.includes(p.id));
  // A changing room's HUD: Play Now counts the set's rounds; a Royale round shows
  // how many are left and go out (its final is a first-to-N duel, like any).
  const setup = dynKind ? setupRef.current : null;
  const hudMaxScore = setup && !setup.final ? undefined : matchConfig.maxScore;
  const hudMaxRounds = setup && setup.dynamic === "playnow" ? setup.setRounds : matchConfig.maxRounds;
  const hudHeadline = setup && setup.dynamic === "royale" && !setup.final ? { label: "Eliminations", value: setup.out ?? 0 } : undefined;
  const getRenderTime = () => remote.getRenderTime();
  const youKeys: Record<string, { left: string | null; right: string | null } | null> = Object.fromEntries(localIdsRef.current.map((id, i) => {
    const lp: any = localPlayersRef.current[i];
    return [id, touch || !lp ? null : { left: lp.left ?? null, right: lp.right ?? null }];
  }));
  // two players on one phone: the board goes, each gets a strip at their end
  const couch = touch && localIdsRef.current.length >= 2;
  const scoreProps = {
    gameState,
    players,
    renderTime: getRenderTime,
    startScore: roundStartScoreRef.current,
    finalScore: roundOverScoreRef.current,
    roundOver: !!roundOverScoreRef.current,
    tieBreak: !dynKind && !!roundOverScoreRef.current && isTieBreak(roundOverScoreRef.current, matchConfig.maxScore, matchConfig.maxRounds, roundRef.current),
    maxScore: hudMaxScore,
    maxRounds: hudMaxRounds,
    youIds: localIdsRef.current,
    youKeys,
    markEliminated: true,
    roundIntro: true,
    // the Royale's big field: the rows go to their new places in one move
    oneMoveSort: dynKind === "royale",
  };

  return (
    <RenderErrorBoundary>
      {debug && (
        <DebugOverlay
          remote={remote}
          youId={localIdsRef.current.join(",")}
          phase={phase}
          getOneWay={() => oneWayRef.current}
          getOwnNow={ownNow}
          drainUpdGap={() => { const g = updGapRef.current; const m = g.maxGap; g.maxGap = 0; return m; }}
        />
      )}
      <GameLayout
      hud={
        <GameHud
          gameState={gameState}
          roundNumber={roundRef.current}
          totalPlayers={players.length}
          maxScore={hudMaxScore as number}
          maxRounds={hudMaxRounds}
          headline={hudHeadline}
          renderTime={getRenderTime}
          players={players}
          winners={padWinners(roundWinnersRef.current, roundOverScoreRef.current ?? startScoreRef.current)}
        />
      }
      arena={
        <GameCanvas
          config={{ fullscreen: true, introDelay: (id: string) => introDelaysRef.current[id] }}
          gameState={gameState}
          players={players}
          renderTime={getRenderTime}
          localPlayerIds={predict ? localIdsRef.current : undefined}
          predictors={predict ? predictorsRef.current : undefined}
          debugPredicted={debug}
          serverGhost={ghost ? { getSegment: remote.getServerLastSegment, getTime: remote.getServerTime } : undefined}
          powerUpTruthTime={ownNow}
          contentFade={phase === "countdown" && !finalCountdownRef.current ? contentFadeFor(countdownSecsRef.current) : undefined}
          getTargetTime={ownNow}
          adaptiveDelay={adaptiveDelay}
          adaptMarginSec={adaptMarginSec}
          serverTime={remote.getServerTime}
          interpStatsRef={interpStatsRef}
        >
          {/* Count down on the pure-local countdown clock (countdownNow) - plain elapsed
              wall time since game_start, un-slewed - so the number ticks perfectly evenly
              like local play, instead of stuttering when the worm clock (ownNow) slews to
              the server. GO still lands as your (client-authoritative) worm starts moving,
              since both are anchored at the same game_start instant. NOT the render clock,
              which lags by latency + render delay. */}
          <Countdown gameState={gameState} getRenderTime={countdownNow} />
          <RespawnCountdown gameState={gameState} youIds={localIdsRef.current} getRenderTime={getRenderTime} />
          {/* keys tutorial: no name tags on the board, only the key cards */}
          {!(firstRun && !touch) && <SpawnTags key={roundRef.current} gameState={gameState} players={players} schedule={introScheduleRef.current} leaderIds={introLeaders(players, roundStartScoreRef.current)} getRenderTime={getRenderTime} youTag={firstRun && touch && localIdsRef.current.length === 1 ? { ids: localIdsRef.current, label: "This is you" } : undefined} />}
          <FirstRunHints gameState={gameState} players={players} youIds={localIdsRef.current} youKeys={youKeys} touch={touch} phase={hint} delays={introDelaysRef.current} getRenderTime={getRenderTime} />
          {phase === "countdown" && roundWinnerRef.current && (() => {
            const secs = Math.max(0, Math.ceil((nextRoundAtRef.current - Date.now()) / 1000));
            const winner = roundWinnerRef.current;
            // A ranked duel gets the fight card: the two scores, big.
            const duel = !!room && /^public:ranked-duel/.test(room) && (matchConfig.players || []).length === 2;
            if (duel) {
              const ps: any[] = matchConfig.players;
              const mine = ps.find((p: any) => localIdsRef.current.includes(p.id));
              const [l, r] = mine ? [mine, ps.find((p: any) => p !== mine)] : ps;
              const before = roundBeforeScoreRef.current || {};
              const after = roundOverScoreRef.current || startScoreRef.current || {};
              const side = (p: any) => ({ id: p.id, name: p.name, colorId: p.colorId, before: before[p.id] ?? 0, after: after[p.id] ?? 0 });
              return (
                <Overlay gameState={gameState} className="canvas-overlay-text canvas-overlay-above-wall">
                  <DuelFightCard
                    left={side(l)}
                    right={side(r)}
                    round={roundRef.current}
                    maxScore={matchConfig.maxScore || 3}
                    totalSeconds={countdownSecsRef.current}
                  />
                </Overlay>
              );
            }
            return (
              <Overlay gameState={gameState} className="canvas-overlay-text canvas-overlay-above-wall">
                <RoundOverCard
                  winnerName={winner.name}
                  winnerColorId={winner.colorId}
                  winnerFlag={winner.flag}
                  roundNumber={roundRef.current}
                  roundPoints={roundWinnerPointsRef.current}
                  final={finalCountdownRef.current}
                  secondsLeft={secs}
                  totalSeconds={countdownSecsRef.current}
                  tie={!dynKind && !finalCountdownRef.current && isTieBreak(roundOverScoreRef.current, matchConfig.maxScore, matchConfig.maxRounds, roundRef.current)}
                />
              </Overlay>
            );
          })()}
        </GameCanvas>
      }
      score={<Score {...scoreProps} />}
      couchStrips={couch ? { bottom: <Score {...scoreProps} only={localIdsRef.current[0]} />, top: <Score {...scoreProps} only={localIdsRef.current[1]} /> } : undefined}
      map={gameState.map}
      playstyle={matchPlaystyle(matchConfig)}
      ranked={!!room && /^public:ranked-/.test(room)}
      onLeave={leaveSession}
      // Leaving counts as finishing last (the server rates it so, see
      // match-runner's `left`); say so before anyone walks out.
      leaveNote={dynKind === "playnow" ? undefined : dynKind === "royale" ? (localIdsRef.current.length ? "Leaving means you are out of this Royale." : undefined) : "Leaving the game counts as a loss!"}
      // Two players on one phone: the board would sit under player 2's
      // steering prints (the top half is theirs), so it goes, as in local play.
      // And between the rounds of a changing room the pause's card has the
      // standings: the phone's own board would only repeat them under it.
      hideScoreCompact={couch || (!!dynKind && phase === "countdown")}
      roundOver={phase === "countdown"}
      roundOverTiming={{ totalSeconds: countdownSecsRef.current }}
      // Live once the round's countdown starts (own clock past the roll call).
      getLive={() => {
        const gs: any = remoteRef.current?.getGameState();
        return phaseRef.current === "playing" && !!gs && ownNow() >= (gs.rollCallTime ?? 0);
      }}
      settingsPlayers={humans}
      touchControls={
        touch ? (
          <TouchSteeringOverlay players={humans} onSteeringUpdate={(playerId: any, s: number) => sendSteeringRef.current(playerId, s)} hint={hint === "keys" ? null : hint} />
        ) : undefined
      }
      />
      {/* Netstats overlay (F9): the adaptive render-delay internals, live - per
          opponent measured cushion, data age/peaks, holds. Floating, fixed
          footprint, read-only. */}
      {netStats && (
        <NetStatsOverlay
          statsRef={interpStatsRef}
          serverNow={() => remoteRef.current!.getServerTime()}
          getGameState={() => remoteRef.current!.getGameState()}
          players={matchConfigRef.current?.players || []}
          crowdLevel={matchConfigRef.current?.crowdLevel}
          localIds={localIdsRef.current}
          updGapRef={updGapRef}
          hostPaceRef={hostPaceRef}
          getOneWay={() => oneWayRef.current}
        />
      )}
      {/* A changing room's own screens, over the game: the next round's reveal,
          who is out, the set podium, the Royale's final and its winner. */}
      {royaleOpening}
      {dynKind && (
        <DynamicLayer
          kind={dynKind}
          phase={phase === "over" ? "over" : phase === "countdown" ? "countdown" : "playing"}
          setup={setupRef.current}
          next={phase === "countdown" ? nextRef.current : null}
          result={phase === "countdown" || phase === "over" ? roundResultRef.current : null}
          royaleResult={royaleResultRef.current}
          players={players}
          playerById={knownPlayersRef.current}
          score={roundOverScoreRef.current ?? startScoreRef.current ?? {}}
          roundWinnerId={phase === "countdown" && roundWinnerDynRef.current ? roundWinnerDynRef.current.id : null}
          roundWinnerPoints={phase === "countdown" ? roundWinnerPointsRef.current : null}
          scoreBefore={phase === "countdown" ? roundBeforeScoreRef.current : null}
          seatIds={seatIdsRef.current}
          youIds={localIdsRef.current}
          pending={pendingRef.current}
          myPlace={myPlaceRef.current}
          pauseStartedAt={pauseAtRef.current}
          pauseMs={countdownSecsRef.current * 1000}
          moved={movedRef.current}
          touch={touch}
          getGameState={() => remoteRef.current?.getGameState()}
          resultSaved={royaleSavedRef.current}
          onLeave={leaveSession}
          onAgain={onPlayAgain ? playAgain : undefined}
          onPlayNow={onPlayNow}
          onResults={() => setDynResults(true)}
          hasSetResults={!!lastSetRef.current}
        />
      )}
      {/* The changing rooms' results, over the game (it runs on underneath). */}
      {dynKind && dynResults && (() => {
        const back = () => setDynResults(false);
        if (dynKind === "playnow") {
          const set = lastSetRef.current;
          if (!set) return null;
          const fake: any = { getCurrentScore: () => Object.fromEntries(set.standings.map((s) => [s.id, s.score])), matchConfig: { players: set.players }, matchState: { roundsData: [] }, isMatchOver: () => true };
          return (
            <div className={gameStyles.dynResults}>
              <MatchOver sound={false} match={fake} onRoundClick={() => {}} onExitAction={leaveSession} exitLabel="Leave" onBack={back}
                kicker={`Play Now · match ${set.set}`} matchStats={set.stats} statsPlayerIds={seatIdsRef.current.filter((id) => set.standings.some((s) => s.id === id))} mainPlayerId={seatIdsRef.current[0]} />
            </div>
          );
        }
        // The Royale: its placings at the end; before that, who is still in on
        // top and everyone out by place (it fills in when it ends).
        const known = knownPlayersRef.current;
        const end = royaleResultRef.current;
        const outs = royaleOutRef.current;
        const noteOut = (id: string) => { const o = outs[id]; return o ? (o.final ? "Beaten in the final" : `Out in stage ${o.stage}`) : ""; };
        let placings: { id: string; note: string; still?: boolean }[];
        // (the winner is known as the final's last round ends; the placings
        // follow a moment later, with the match's end)
        const winnerId = (end && end.winner) || (roundResultRef.current && roundResultRef.current.winner) || null;
        if (end && end.placements && end.placements.length) {
          placings = end.placements.map((id: string, i: number) => ({ id, note: i === 0 ? "Winner" : i === 1 && !outs[id] ? "Beaten in the final" : noteOut(id) }));
        } else if (winnerId) {
          const rest = players.filter((p: any) => p.id !== winnerId && !outs[p.id]).map((p: any) => ({ id: p.id, note: "Beaten in the final" }));
          const gone = Object.keys(outs).sort((a, b) => outs[a].place - outs[b].place).map((id) => ({ id, note: noteOut(id) }));
          placings = [{ id: winnerId, note: "Winner" }, ...rest, ...gone];
        } else {
          const still = players.filter((p: any) => !outs[p.id]).map((p: any) => ({ id: p.id, note: "Still in", still: true }));
          const gone = Object.keys(outs).sort((a, b) => outs[a].place - outs[b].place).map((id) => ({ id, note: noteOut(id) }));
          placings = [...still, ...gone];
        }
        const all = placings.map((pl) => known[pl.id]).filter(Boolean);
        const fake: any = { getCurrentScore: () => Object.fromEntries(all.map((p: any) => [p.id, 0])), matchConfig: { players: all }, matchState: { roundsData: [] }, isMatchOver: () => !!end };
        const num = (end && end.number) || (setupRef.current && setupRef.current.number);
        return (
          <div className={gameStyles.dynResults}>
            <MatchOver sound={false} match={fake} onRoundClick={() => {}} placings={placings} heroSub="wins the Royale" kicker={num ? `Royale #${num}` : "Royale"}
              onExitAction={leaveSession} exitLabel="Back to start" onBack={back} backLabel={winnerId ? "Back" : "Back to the game"}
              onPlayAgain={winnerId && onPlayAgain ? playAgain : undefined} playAgainLabel="Join next Royale"
              // (your seats in the room: once out you no longer play a round, but the rows are still yours)
              matchStats={end ? matchStatsRef.current : null} statsPlayerIds={seatIdsRef.current} mainPlayerId={seatIdsRef.current[0]} />
          </div>
        );
      })()}
      {/* What the match moved on your achievements: cards in the corner. In
          Play Now and the Royale that is after a set, or when your run ends,
          while the room plays on. */}
      {(phase === "over" || dynKind) && movesRef.current && localIdsRef.current[0] && <AchievementProgress key={movesSeqRef.current} moves={movesRef.current[localIdsRef.current[0]]} />}
      {/* Stage 1 of the match-end flow: the modal over the frozen arena. Its
          button flips resultsOpen, which swaps this render for MatchOver. */}
      {phase === "over" && rated && !resultsOpen && (
        <MatchEndModal
          players={matchConfig.players}
          score={startScoreRef.current || {}}
          myPlayerId={localIdsRef.current[0] ?? null}
          myMove={(() => {
            const myId = localIdsRef.current[0];
            const u = ratingUpdatesRef.current;
            return myId && u ? u[myId] ?? null : null;
          })()}
          ratingsArrived={ratingUpdatesRef.current != null}
          myUnlocks={(() => {
            const myId = localIdsRef.current[0];
            const u = unlocksRef.current;
            return myId && u ? u[myId] ?? null : null;
          })()}
          // The room names its ladder up front, so the card is titled with it
          // before rating_update lands; until then it shows the rank you
          // brought into the match (ranked_roster).
          modeLabel={rankedLabel(ratingModeRef.current ?? /^public:ranked-(duel|ffa)/.exec(room || "")?.[1]) ?? undefined}
          currentRank={(() => {
            const myId = localIdsRef.current[0];
            const r = rankedRosterRef.current;
            return myId && r ? r[myId] ?? null : null;
          })()}
          onSeeResults={() => setResultsOpen(true)}
          onPlayAgain={onPlayAgain ? playAgain : undefined}
        />
      )}
    </RenderErrorBoundary>
  );
}

// Memoized so re-renders of an embedding lobby (its card counts tick every second)
// don't re-render the live game and thrash the canvas. Props are kept stable by
// the caller (stable roomId string + onSession/onLeave callbacks).
export default React.memo(RemoteGame);

// Surfaces a render-path exception as on-screen text instead of a blank canvas,
// so a problem in the streamed-state renderers is debuggable on a phone.
class RenderErrorBoundary extends React.Component<{ children: React.ReactNode }, { err: any }> {
  constructor(props: any) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err: any) {
    return { err };
  }
  componentDidCatch(err: any, info: any) {
    reportError(err, { where: "game render", componentStack: info && info.componentStack });
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 16, color: "#ff6b6b", font: "12px monospace", whiteSpace: "pre-wrap", overflow: "auto", maxHeight: "100vh" }}>
          {"Render error:\n" + String(this.state.err?.stack || this.state.err)}
        </div>
      );
    }
    return this.props.children as any;
  }
}

// A small fixed overlay (enabled with ?debug=1) that polls live remote-game state
// each frame, so we can see on a phone whether state is arriving and advancing.
function DebugOverlay({ remote, youId, phase, getOneWay, getOwnNow, drainUpdGap }: { remote: any; youId: any; phase: string; getOneWay: () => number; getOwnNow?: () => number; drainUpdGap?: () => number }) {
  const [, tick] = useState(0);
  // Rolling FPS + worst (longest) frame over the last second, to spot frame-pacing
  // hitches that read as a laggy "feel" even when each frame looks fine.
  const fpsRef = useRef({ last: 0, frames: 0, fps: 0, worst: 0, worstShown: 0, t0: 0 });
  // Own-worm clock health over the last second. The predicted head is drawn at
  // ownNow(), so if that clock stalls or crawls, YOUR worm freezes for those frames:
  // felt as input latency / stutter even at a solid fps. Per frame we compare how far
  // ownNow advanced vs wall time; a frame advancing under half real speed is a "hold".
  const clockRef = useRef({ lastOwn: 0, holds: 0, worstAdv: 1, holdsShown: 0, worstAdvShown: 1, updGapShown: 0 });
  useEffect(() => {
    let raf = 0;
    const loop = (now: number) => {
      const f = fpsRef.current;
      const c = clockRef.current;
      if (f.last) {
        const dt = now - f.last;
        if (dt > f.worst) f.worst = dt;
        if (getOwnNow && dt > 0 && dt < 250) {
          const own = getOwnNow();
          if (c.lastOwn > 0) {
            const adv = ((own - c.lastOwn) * 1000) / dt; // 1.0 = real-time speed
            if (adv < c.worstAdv) c.worstAdv = adv;
            if (adv < 0.5) c.holds++;
          }
          c.lastOwn = own;
        }
      }
      f.last = now;
      f.frames++;
      if (now - f.t0 >= 1000) {
        f.fps = Math.round((f.frames * 1000) / (now - f.t0));
        f.worstShown = Math.round(f.worst);
        c.holdsShown = c.holds;
        c.worstAdvShown = c.worstAdv;
        c.updGapShown = drainUpdGap ? Math.round(drainUpdGap()) : 0;
        c.holds = 0;
        c.worstAdv = 1;
        f.frames = 0;
        f.worst = 0;
        f.t0 = now;
      }
      tick((x) => x + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const gs = remote.getGameState();
  const f = fpsRef.current;
  const c = clockRef.current;
  const lines = [
    `build ${BUILD_LABEL}`,
    `fps: ${f.fps}  worst frame: ${f.worstShown}ms`,
    // holds = frames (last second) where your worm's clock ran under half speed;
    // slowest = its worst per-frame speed (1.00 = real time). upd gap = the longest
    // wait between server updates in that second (steady stream ~45ms).
    `own clock holds: ${c.holdsShown}/s  slowest: x${c.worstAdvShown.toFixed(2)}  upd gap: ${c.updGapShown}ms`,
    `phase: ${phase}  you: ${String(youId)}`,
    `one-way latency: ${Math.round(getOneWay() * 1000)}ms`,
    gs ? `gameTime: ${(gs.gameTime ?? 0).toFixed(2)}  renderT: ${remote.getRenderTime().toFixed(2)}` : "gameState: null",
    gs ? `worms: ${gs.worms?.length}  active: ${gs.gameActive}` : "",
    gs ? `map: ${gs.map?.width}x${gs.map?.height} (${gs.map?.shape?.type})` : "",
    gs ? `seg tracks: ${Object.keys(gs.wormPathSegments || {}).length}  events: ${gs.gameEvents?.length}` : "",
    // How many tracks claim one of MY ids - should be exactly one per local worm.
    // More than that means a duplicate own-worm track (a ghost source).
    gs ? `own tracks: ${Object.values(gs.wormPathSegments || {}).filter((s: any) => s[0] && String(youId).split(",").includes(s[0].playerId)).length}` : "",
  ];
  return (
    <div style={{ position: "fixed", top: 0, left: 0, zIndex: 99999, background: "rgba(0,0,0,0.75)", color: "#39ff14", font: "11px/1.4 monospace", padding: "6px 8px", whiteSpace: "pre", pointerEvents: "none" }}>
      {lines.join("\n")}
    </div>
  );
}

// Parse an integer URL param, falling back to `def` and clamping to [min, max].
function clampInt(raw: string | null, def: number, min: number, max: number): number {
  const n = raw === null ? def : parseInt(raw, 10);
  if (Number.isNaN(n)) return def;
  return Math.max(min, Math.min(max, n));
}

// Netstats overlay (F9): the adaptive render-delay internals, live. One row per
// worm track: how far behind server-now it renders, the measured jitter cushion,
// the current/peak data age feeding that cushion, and how many frames the clock
// starved (data late beyond the cushion). Own worms are predicted, not delayed -
// their rows are tagged and dimmed. Fixed footprint, read-only, poll-driven.
function NetStatsOverlay({ statsRef, serverNow, getGameState, players, crowdLevel, localIds, updGapRef, hostPaceRef, getOneWay }: {
  statsRef: { current: ((serverNow: number) => any[]) | null };
  serverNow: () => number;
  getGameState: () => any;
  players: any[];
  // the match's own bot level, when the host decided one (core ai/crowd.js)
  crowdLevel?: number;
  localIds: string[];
  updGapRef: { current: { last: number; maxGap: number; gaps: number[] } };
  hostPaceRef?: { current: { wall: number; gameTime: number; ticks: number; sentAt: number }[] };
  getOneWay?: () => number;
}) {
  const [sent, setSent] = useState({ avg: 0, p95: 0, worst: 0, rtt: 0 });
  // The host's cadence for this roster (the crowd ladder): the overlay's targets.
  const cadence = hostCadence(players.length, crowdLevel);
  const [rows, setRows] = useState<any[]>([]);
  const [arrivals, setArrivals] = useState({ avg: 0, p95: 0, worst: 0 });
  const [host, setHost] = useState({ speed: 1, ticksPerSec: 0, updatesPerSec: 0 });
  useEffect(() => {
    const t = setInterval(() => {
      const fn = statsRef.current;
      if (!fn) return;
      const gs = getGameState();
      const stats = fn(serverNow()) || [];
      // OPPONENT rows only: own worms are predicted (their interp clock is unused),
      // and a dead worm's frozen track just reads as a huge stale age - both noise.
      const named = stats.flatMap((s: any) => {
        const segs = gs && gs.wormPathSegments && gs.wormPathSegments[s.trackId];
        const pid = segs && segs[0] ? segs[0].playerId : null;
        if (pid == null || localIds.includes(pid)) return [];
        const worm = gs.worms && gs.worms.find((w: any) => w.trackId === s.trackId);
        if (worm && worm.alive === false) return [];
        const player = players.find((p: any) => p.id === pid);
        return [{ ...s, name: player ? player.name : String(s.trackId) }];
      });
      named.sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));
      setRows(named);
      // Packet arrival stats over the recent ring (~10s): the INPUT the cushion
      // answers - how often updates arrive and how unreliable the spacing is.
      // Non-destructive read (maxGap stays for the ?debug overlay's drain).
      const gaps = updGapRef.current.gaps;
      if (gaps.length) {
        const sorted = [...gaps].sort((a, b) => a - b);
        setArrivals({
          avg: Math.round(gaps.reduce((s, v) => s + v, 0) / gaps.length),
          p95: Math.round(sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]),
          worst: Math.round(sorted[sorted.length - 1]),
        });
      }
      // The host's pace over the same window: game seconds delivered per wall
      // second (1.00 = real time), its tick rate and its push rate.
      const hp = hostPaceRef ? hostPaceRef.current : [];
      if (hp.length >= 2) {
        const first = hp[0], last = hp[hp.length - 1];
        const wallS = (last.wall - first.wall) / 1000;
        let ticks = 0;
        for (let i = 1; i < hp.length; i++) ticks += hp[i].ticks;
        // The host's OWN push spacing (its send stamps): if this is even while the
        // arrival spacing above bunches, the bunching is the network; if the host's
        // spacing bunches too, it is the host.
        const sgaps: number[] = [];
        for (let i = 1; i < hp.length; i++) if (hp[i].sentAt && hp[i - 1].sentAt) sgaps.push(hp[i].sentAt - hp[i - 1].sentAt);
        if (sgaps.length) {
          const ss = [...sgaps].sort((a, b) => a - b);
          setSent({ avg: Math.round(sgaps.reduce((s, v) => s + v, 0) / sgaps.length), p95: Math.round(ss[Math.min(ss.length - 1, Math.floor(ss.length * 0.95))]), worst: Math.round(ss[ss.length - 1]), rtt: getOneWay ? Math.round(getOneWay() * 2000) : 0 });
        }
        if (wallS > 0.5) {
          setHost({
            speed: Math.round(((last.gameTime - first.gameTime) / wallS) * 100) / 100,
            ticksPerSec: Math.round(ticks / wallS),
            updatesPerSec: Math.round(((hp.length - 1) / wallS) * 10) / 10,
          });
        }
      }
    }, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pad = (v: any, n: number) => String(v).padStart(n);
  const lines = [
    "NETSTATS (F9)",
    "updates: avg " + arrivals.avg + "ms · p95 " + arrivals.p95 + "ms · worst " + arrivals.worst + "ms (10s)",
    "host pace: x" + host.speed.toFixed(2) + " real time · " + host.ticksPerSec + " ticks/s (target " + cadence.ticksPerSec + ") · " + host.updatesPerSec + " updates/s (target " + cadence.pushesPerSec + ")",
    // (No host-side spacing line: on Cloudflare the host's clock inside a timer
    // callback is the scheduled time, so its send stamps always read an even
    // cadence, stalls included. The arrival line above is the truth.)
    "rtt " + sent.rtt + "ms",
    "opponent        delay cushion   age  peak holds",
    ...(rows.length
      ? rows.map((r) => "  " + String(r.name).padEnd(13).slice(0, 13) +
          pad(r.delayMs, 6) + pad(r.cushionMs, 8) + pad(r.ageMs, 6) + pad(r.peakMs, 6) + pad(r.holds, 6))
      : ["  (no live opponents)"]),
  ];
  return (
    <div style={{ position: "fixed", bottom: 12, left: 12, zIndex: 99998, background: "rgba(7,10,18,0.85)", border: "1px solid #26304a", borderRadius: 8, color: "#9fd4ff", font: "11px/1.5 monospace", padding: "8px 10px", whiteSpace: "pre", pointerEvents: "none", minWidth: 340 }}>
      {lines.join("\n")}
    </div>
  );
}
